/**
 * Collaborative authoring for one revision's body.
 *
 * EDITABLE ONLY WHILE t1 SAYS SO. The verdict arrives with the body from Get Document Content --
 * DRAFT, and no approval request pending against it. It is NOT recomputed here: the rule lives in
 * t1 where the lifecycle lives, and a second copy of it in the browser would be a second thing to
 * keep in step. This component only renders the answer.
 *
 * AND THE BROWSER IS NOT THE GUARD ANYWAY. Making the editor read-only stops an honest person
 * typing into a frozen document; it stops nobody else. Save Document Content asks t1 again on the
 * server before it writes, and that is the check that counts. This one exists so the person is not
 * invited to do work that will be refused.
 */
import React, { useEffect, useMemo, useReducer, useRef, useState, useCallback} from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Document from "@tiptap/extension-document";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import { Table, TableCell, TableHeader, TableRow } from "@tiptap/extension-table";
import TextAlign from "@tiptap/extension-text-align";
import Highlight from "@tiptap/extension-highlight";
import DocumentFigure, { rememberFigures, unresolvedFigures } from "./DocumentFigure";
import { Tab, TabWidths } from "./TabStops";
import { ACCEPT_ATTRIBUTE, rejectionReason } from "./figureUpload";
import { Color, FontFamily, FontSize, LineHeight, TextStyle } from "@tiptap/extension-text-style";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import * as Y from "yjs";
import { Awareness } from "y-protocols/awareness";
import { Alert, Box, Button, Chip, CircularProgress, Stack } from "@mui/material";

import { YMqttProvider } from "../../util/YMqttProvider";
import EditorToolbar, { SIZES } from "./EditorToolbar";
import EditorMenuBar, { MenuAction, MenuDefinition } from "./EditorMenuBar";
import FindReplace from "./FindReplace";
import DocumentPaper from "./DocumentPaper";
import DocumentFooter from "./DocumentFooter";
import { DOCUMENT_CONTENT, DocField, DocFooter, DocHeader, FIELD_LABELS,
         PAGE_SETUP_ATTRIBUTES, hasFooter, hasHeader, insertField, insertFooter, insertHeader,
         pageSetup, removeFooter, removeHeader, setDocumentFieldValues,
         setPageSetup, setParagraphStyles } from "./PageElements";
import PageSetupDialog from "./PageSetupDialog";
import TableOptionsPanel from "./TableOptionsPanel";
import { TableBorders, selectedBorder, selectedBorderValue,
         setSelectedBorder } from "./TableBorders";
import { GlossaryMarks, setGlossary } from "./GlossaryMarks";
import type { GlossaryTerm } from "./glossary";
import { ColumnTypeMarks, TableAttributes, TableGripper, applyQuickLayout, cellAttribute, columnWidth,
         currentColumnType, setCurrentColumnType, sortRows, sortableReason,
         rowMinHeight, setRowMinHeight, tableAt,
         setCellAttribute, setColumnWidth, setTableLook, tableHasHeader,
         tableLook } from "./TableOptions";
import EditorRuler from "./EditorRuler";
import SuggestionBubble from "./SuggestionBubble";
import VersionHistory, { Version } from "./VersionHistory";
import { DocAttrStep } from "@tiptap/pm/transform";
import { startUpdateLog, UpdateLog } from "./updateLog";
import { CommentAnchor } from "./CommentMark";
import { OutlineNumbers } from "./OutlineNumbers";
import { CrossReference, CrossReferenceText, HeadingAnchor, ensureAnchor } from "./CrossReference";
import { Footnote, FootnoteNumbers } from "./Footnote";
import { STYLE_LABELS, STYLE_NAMES, StyleName, captured, cssVariables, isDefault, styleFor,
         stylesOf } from "./namedStyles";
import { TableOfContents, TableOfContentsText } from "./TableOfContents";
import { BookmarkAnchor, newBookmarkId } from "./BookmarkMark";
import CrossReferenceDialog from "./CrossReferenceDialog";
import FootnoteDialog from "./FootnoteDialog";
import TemplateDialog, { Template } from "./TemplateDialog";
import { notesOf } from "./footnotes";
import { bookmarksOf, outlineOf } from "./outline";
import CommentComposer from "./CommentComposer";
import CommentPanel, { CommentThread } from "./CommentPanel";
import OutlinePane from "./OutlinePane";
import renderVersion from "./renderVersion";
import { SuggestionTracking } from "./SuggestionExtension";
import { PasteStructure } from "./PasteStructure";
import { Pagination, measurePagination } from "./pagination";
import PageBreak from "./PageBreak";
import { BlockSpacing, Subscript, Superscript, hasTitleRow, indentBlocks, insertTitleRow,
         setCase, setParagraphSpace } from "./Formatting";
import { Deletion, Insertion, SuggestionMode, allSuggestions, resolveAll } from "./suggestions";
import { watermarkFor } from "./watermark";
import { pageGeometry } from "./pageGeometry";

/**
 * How long to let peers answer before deciding this browser must seed the document.
 *
 * Short enough that a solo editor does not stare at an empty page; long enough for a hello and
 * its reply to cross IoT Core. Seeding when somebody else already holds the document is the
 * expensive mistake -- it doubles the text irreversibly -- so the wait is the safe side.
 */
const PEER_GRACE_MS = 1500;

/**
 * How long to wait for the MQTT connection to come up before giving up on asking peers at all.
 *
 * Generous, because the cost of waiting is a second of blank page for a solo author and the cost of
 * not waiting is a doubled document. Bounded, because a tenant with no MQTT must still be able to
 * write.
 */
const ANNOUNCE_WAIT_MS = 6000;

/** How long the document must be still before a save. */
const SAVE_AFTER_MS = 2000;

/** Colours for remote carets. Fixed order, assigned by client id, never generated. */
const CARET_COLOURS = ["#5ABCED", "#E8833A", "#43A047", "#8E6FD8", "#D81B60", "#00897B"];

export type DocumentEditorProps = {
    documentUSID: string;
    /** The revision, for File > New and File > Open. */
    revisionUSID?: string;
    /** File menu actions the PAGE owns, because they navigate or change the set of documents. */
    onNewDocument?: () => void;
    /** Start a new document on this revision from a template. */
    onNewFromTemplate?: (templateUSID: string) => void;
    /** Opens the "Create from existing document" picker, which the PAGE owns -- it spans every
     *  revision, and this component is scoped to one. */
    onNewFromExisting?: () => void;
    /** Whether this document is one of the shells others start from. */
    isTemplate?: boolean;
    onSetTemplate?: (next: boolean) => void;
    onRename?: () => void;
    onDelete?: () => void;
    /** Why Delete is off, when it is. Said by the page, which knows how many parts there are. */
    deleteUnavailable?: string;
    onImported?: () => void;
    /** When the body last reached the server. Shown on the record line, at the far right. */
    onSaved?: (at: number | null) => void;
    /** File ▸ Make a copy: duplicate this part on the same revision. */
    onDuplicate?: () => void;
    /** File ▸ Open: back to the list of documents. */
    onOpenList?: () => void;
    /** The QMS record, for the footer's identity line. */
    record?: { ObjectID?: string | null; Name?: string | null; DisplayRevision?: string | null } | null;
    /** The revision's status, so a draft says so on every printed page. */
    status?: string | null;
    /** The mqtt.js client from useMqttClient. Null disables collaboration, not editing. */
    mqttClient: any;
    sessionContext: any;
    /** Get Document Content / Save Document Content, injected so this renders in a test. */
    api: {
        get: (documentUSID: string) => Promise<any>;
        save: (documentUSID: string, body: any, expectedDigest: string | null,
               acknowledgeLoss?: boolean, source?: string) => Promise<any>;
        importDocx?: (documentUSID: string, base64: string, filename: string) => Promise<any>;
        /** One figure, straight to S3. Absent in a test, which is why Image says why it is off. */
        /** The conversations about this document. */
        comments?: (documentUSID: string) => Promise<any>;
        createComment?: (documentUSID: string, body: string, anchorText: string,
                         mentions?: string[]) => Promise<any>;
        replyToComment?: (commentUSID: string, body: string, mentions?: string[]) => Promise<any>;
        /** Who the "@" picker may offer. A suggestion list -- the server decides at the write. */
        mentionablePeople?: (documentUSID: string, search: string) => Promise<any[]>;
        resolveComment?: (commentUSID: string, resolved: boolean) => Promise<any>;
        /** Append a flush of Yjs updates, and optionally the whole state as a checkpoint. */
        recordUpdates?: (documentUSID: string, updates: string[], checkpoint?: string) => Promise<any>;
        /** Put a version's text into a new part of this revision, and go to it. */
        copyToNewPart?: (name: string, body: any) => Promise<any>;
        /** Rebuild the document as it stood at a moment, from the operation log. */
        replayAt?: (documentUSID: string, at?: string) => Promise<any | null>;
        uploadFigure?: (documentUSID: string, file: File) =>
            Promise<{attachment: string; url: string; width: number | null}>;
        /** Fresh display URLs, for a session that outlives the hour a presigned one lasts. */
        figureUrls?: (documentUSID: string, attachments: string[]) => Promise<any>;
        rendition?: (documentUSID: string) => Promise<any>;
        /** The document as a Word file — a convenience copy, stamped as one. */
        wordExport?: (documentUSID: string) => Promise<any>;
        /** Every document in the tenant somebody has marked as a shell to start from. */
        templates?: () => Promise<any>;
        // OPTIONAL, like templates above it. A reading aid that a caller has not wired is a
        // document with no marks, not a document that will not open.
        glossary?: (revisionUSID: string) => Promise<any>;
    };
};

/**
 * The document as it is stored: every attribute that is not set, left out.
 *
 * PROSEMIRROR SERIALISES EVERY DECLARED ATTRIBUTE, including the ones nobody has set -- so the day
 * indent, spacing, table alignment, border weight and cell padding were added as attributes, every
 * paragraph and every cell in every document started carrying half a dozen explicit nulls. The text
 * did not change by one character and the stored body grew by a quarter.
 *
 * THAT IS NOT A SIZE PROBLEM, IT IS A DIGEST PROBLEM. The ContentDigest is taken over the body, and
 * t1's approval hash is taken over the digest -- so simply OPENING a document rewrote it, moved the
 * digest and would have marked a pending approval stale. A document nobody edited must come back
 * out the way it went in.
 *
 * Null and undefined only: `false` and `0` are answers, and a quick layout that sets an indent of
 * zero means zero.
 */
function pruned(node: any): any {
    if (Array.isArray(node)) return node.map(pruned);
    if (!node || typeof node !== "object") return node;
    const out: any = {};
    for (const [key, value] of Object.entries(node)) {
        if (key === "attrs" && value && typeof value === "object") {
            const attrs: any = {};
            for (const [name, setting] of Object.entries(value as object)) {
                if (setting !== null && setting !== undefined) attrs[name] = setting;
            }
            if (Object.keys(attrs).length) out.attrs = attrs;
            continue;
        }
        out[key] = pruned(value);
    }
    return out;
}

export default function DocumentEditor({ documentUSID, revisionUSID, mqttClient, sessionContext,
                                        api, onNewDocument, onNewFromTemplate, onNewFromExisting,
                                        isTemplate,
                                        onSetTemplate, onRename, onDelete, deleteUnavailable,
                                        onImported, onSaved, onDuplicate, onOpenList,
                                        record, status }: DocumentEditorProps) {

    const [loading, setLoading] = useState(true);
    const [editable, setEditable] = useState(false);
    const [reason, setReason] = useState<string | null>(null);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [peers, setPeers] = useState<string[]>([]);
    const [finding, setFinding] = useState(false);
    const [pageOptions, setPageOptions] = useState(false);
    const [templatesOpen, setTemplatesOpen] = useState(false);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    /**
     * This window is holding a copy of the document that is no longer the stored one.
     *
     * NOTHING IS SAVED WHILE THIS IS TRUE, and that is the whole point of knowing it. The text on
     * screen is not wrong -- it is somebody's work -- but it was built on a version the store has
     * moved past, and writing it back would undo whatever moved it. Reloading is the only safe
     * answer and it is the person's to make, so this says so and stops.
     */
    const [stale, setStale] = useState(false);
    const [tableOptions, setTableOptions] = useState(false);
    const [composing, setComposing] = useState<
        { from: number; to: number; anchored: string } | null>(null);
    const [glossaryTerms, setGlossaryTerms] = useState<GlossaryTerm[]>([]);
    /** Whether the reader wants the glossary shown. A personal view setting like full width, so it
     *  is remembered per browser and never travels with the document. */
    const [glossaryOn, setGlossaryOn] = useState(() => {
        try { return localStorage.getItem("docs.glossary") !== "0"; } catch { return true; }
    });
    const [fullWidth, setFullWidth] = useState(() => {
        try { return localStorage.getItem("docs.fullWidth") === "1"; } catch { return false; }
    });
    const [notice, setNotice] = useState<string | null>(null);
    const [zoom, setZoom] = useState(() => {
        try { return Number(localStorage.getItem("docs.zoom")) || 100; } catch { return 100; }
    });
    const [barHidden, setBarHidden] = useState(() => {
        try { return localStorage.getItem("docs.toolbar") === "hidden"; } catch { return false; }
    });
    /** Set when the server refuses a save that would remove most of the document. */
    const [wipe, setWipe] = useState<string | null>(null);
    const [mode, setMode] = useState<SuggestionMode>("editing");
    const [history, setHistory] = useState(false);
    /** The comment panel, the threads on this document, and which one is being looked at. */
    const [comments, setComments] = useState(false);
    /** The outline pane. A view setting: nothing in the document changes. */
    const [outline, setOutline] = useState(false);
    /** The cross-reference picker, and the headings it is choosing between. */
    const [referring, setReferring] = useState(false);
    // THE NOTE BEING WRITTEN OR CHANGED. `at` is the marker's document position, or null
    // while one is being written for the first time -- which is the only difference
    // between the two jobs, and the reason there is one dialog rather than two.
    const [noteEdit, setNoteEdit] = useState<
        { at: number | null; number: number; text: string } | null>(null);
    const [threads, setThreads] = useState<CommentThread[] | null>(null);
    const [threadsLoading, setThreadsLoading] = useState(false);
    const [threadProblem, setThreadProblem] = useState<string | null>(null);
    const [activeThread, setActiveThread] = useState<string | null>(null);
    /** The operation log. Null while the document is read-only -- see the effect that starts it. */
    const log = useRef<UpdateLog | null>(null);
    // READ BY THE MARK ON EVERY RENDER. The extension is configured once at editor creation, so a
    // value captured there would be whatever it was then -- the same reason SuggestionTracking
    // takes a getter rather than a mode.
    const activeThreadRef = useRef<string | null>(null);
    const resolvedThreadsRef = useRef<string[]>([]);
    const [viewing, setViewing] = useState<Version | null>(null);
    /** The body of the version being previewed. Held so the page can render it read-only. */
    const [viewingBody, setViewingBody] = useState<any>(null);
    // Bumped on every transaction so the suggestion count and the toolbar's state follow the
    // document rather than lagging a render behind it.
    // THE COUNT IS READ NOW, not only bumped: the ruler's markers have to follow the caret, and a
    // re-render is not enough on its own -- they need something that CHANGES to recompute from.
    const [editorState, bumpEditorState] = useReducer((n: number) => n + 1, 0);
    const importInput = useRef<HTMLInputElement | null>(null);
    const figureInput = useRef<HTMLInputElement | null>(null);

    // ONE Y.Doc FOR THE LIFETIME OF THIS DOCUMENT. Recreating it would drop every peer's shared
    // history and silently fork the document, which is the one failure a CRDT cannot repair.
    //
    // The revision is the doc's GUID rather than merely its dependency: it is what the document IS,
    // it makes the dependency on it honest rather than something to explain to a linter, and a doc
    // that turns up on the wrong topic is identifiable instead of anonymous.
    const doc = useMemo(() => new Y.Doc({ guid: documentUSID }), [documentUSID]);
    const awareness = useMemo(() => new Awareness(doc), [doc]);

    // READ BY THE TRACKING PLUGIN ON EVERY TRANSACTION. A value captured when the editor was
    // created would freeze the mode at "editing" forever; a ref is what lets the toggle work
    // without rebuilding the editor -- which would drop every peer's shared history.
    const tracking = useRef<{ mode: SuggestionMode; author: { userId: string; userName: string } }>({
        mode: "editing", author: { userId: "", userName: "" },
    });
    tracking.current = {
        mode,
        author: {
            userId: sessionContext?.getAccountUserUSID?.() || String(doc.clientID),
            userName: sessionContext?.getName?.() || "Someone",
        },
    };

    // HELD IN A REF, NOT A DEPENDENCY. The page hands this down as a fresh closure on every
    // render; listing it would restart the debounce effect each time and the document would never
    // settle long enough to save.
    const reportSaved = useRef(onSaved);
    reportSaved.current = onSaved;

    /**
     * What the NEXT save is, when it is not ordinary typing.
     *
     * A restore puts an old body into the editor and lets the debounced save carry it, which is
     * what makes the change reach the other windows. But that save looked exactly like typing, so
     * the audit trail recorded RESTORED nowhere -- every restore read as another SAVED, and "Elena
     * restored the 10:14 version" was a sentence the trail could not produce after all. The flag
     * rides with the one save that follows and is cleared by it.
     */
    const pendingSource = useRef<string | null>(null);

    const digest = useRef<string | null>(null);
    /**
     * The body as it was last loaded or saved, so a save that would change nothing is not made.
     *
     * SEEDING THE EDITOR IS A TRANSACTION, so simply opening a document scheduled an autosave --
     * which wrote the same text back, moved nothing, and left a SAVED row in the audit trail of a
     * controlled document for having looked at it. The server answers `Changed: false` and the
     * digest holds, so nothing was ever lost; it was noise in the one place that must not have any.
     */
    const lastBody = useRef<string | null>(null);
    // READ INSIDE THE DEBOUNCE TIMER, which closes over the value it had when it was scheduled --
    // so the state alone would let one last save through after the window was told it is behind.
    const staleRef = useRef(false);
    /** Who else is in the document right now, readable from inside the debounced save. */
    const peersRef = useRef<string[]>([]);
    /** How many times this window has re-fenced against a save it lost. Bounded; see CC-DOCS-STAL. */
    const refences = useRef(0);
    const saveTimer = useRef<any>(null);
    const seeded = useRef(false);
    const provider = useRef<YMqttProvider | null>(null);

    const editor = useEditor({
        extensions: [
            // History OFF: Yjs keeps the undo stack, and two undo managers over one document
            // disagree about what "undo" means the moment a second person types.
            StarterKit.configure({
                undoRedo: false,
                // THE DOCUMENT NODE IS OURS, because the footer has to be part of the document and
                // the document is what says what may be in it. See PageElements.
                document: false,
            }),
            // THE DOCUMENT NODE CARRIES THE PAGE SETUP, so it travels in the body that is saved
            // and the body that is rendered -- no new column and no second place for a document's
            // settings to live. See pageSetup in PageElements for what that costs.
            Document.extend({
                content: DOCUMENT_CONTENT,
                addAttributes: () => PAGE_SETUP_ATTRIBUTES,
            }),
            // THE HEADER, THE FOOTER AND THEIR FIELDS. Page elements you can write, with the facts
            // QMS owns kept as live fields rather than typed text -- so a revision cannot be
            // frozen into a page.
            DocHeader, DocFooter, DocField,
            // Tables, because a controlled document's revision history IS a table and an import
            // that drops it is not fit for purpose.
            // RESIZABLE, which is what a cursor over a border MEANS. Dragging a column edge writes
            // `colwidth`, which the rendition already reads -- so a column widened here is the same
            // width in the signed PDF. The plugin is also what puts the col-resize cursor on the
            // border and draws the drag line.
            //
            // AND IT REMOVES THE NODE VIEW. prosemirror-tables' TableView applies the table's
            // attributes once in its constructor and never again, which meant a border weight
            // changed in Table options updated the document and left the page as it was; with
            // resizing on, TipTap renders the table through renderHTML instead, which re-renders on
            // every attribute change like any other node. One fewer special case.
            Table.configure({ resizable: true }),
            TableRow, TableHeader, TableCell,
            // The handle at the top-left of every table, as every word processor has.
            TableGripper,
            ColumnTypeMarks,
            // THE APPROVED VOCABULARY, as a decoration rather than a mark -- nothing is
            // written into the document, so a term released tomorrow lights up in a procedure
            // approved yesterday without the procedure changing. See GlossaryMarks.ts.
            GlossaryMarks,
            // POINTING AT AN EDGE. A border belongs to a cell, and until it could be pointed at,
            // the only border anybody could change was the whole table's.
            TableBorders,
            // THE MARKS A PROCEDURE IS ACTUALLY WRITTEN WITH. StarterKit brings bold, italic,
            // underline, strike, headings, lists and links; these are the rest of what the
            // toolbar offers, and nothing beyond it -- an extension with no control is a way for
            // pasted content to carry formatting nobody in this app can see or remove.
            TextStyle, Color, FontFamily, FontSize,
            // LINE SPACING AND CHECKLISTS, because the toolbar offers both and a control that
            // does nothing is worse than an absent one. A checklist is how a procedure's
            // verification steps are actually written.
            LineHeight.configure({ types: ["heading", "paragraph"] }),
            TaskList, TaskItem.configure({ nested: true }),
            // TRACKED CHANGES. The two mark names are a CONTRACT with the server -- resolved(),
            // count_suggestions() and t1's submit gate all key on them -- and registering them
            // closes a live hazard as well as adding a feature: ProseMirror silently discards
            // marks its schema has never heard of, so a body carrying suggestions, opened and
            // saved, would have come back with every one quietly accepted.
            Insertion, Deletion,
            SuggestionTracking.configure({ get: () => tracking.current }),
            // WHAT A PASTED DOCUMENT MEANS, not how it was drawn. Word processors draw the rule
            // under a section title with a one-cell table, so an Instructions For Use pasted in
            // arrived as twenty tables and zero headings. See PasteStructure.ts.
            PasteStructure,
            Highlight.configure({ multicolor: true }),
            // SUPERSCRIPT, SUBSCRIPT, INDENT AND PARAGRAPH SPACING. Written here rather than
            // installed; see Formatting.ts for why, and for why spacing is an ATTRIBUTE.
            Superscript, Subscript, BlockSpacing,
            // WHAT A TABLE LOOKS LIKE, stored on the table so the rendition can draw it the same.
            TableAttributes,
            // FIGURES. The node holds an attachment USID and the bytes live where the record's other
            // files live; `src` is resolved at render time and never written into the body. See
            // DocumentFigure.ts, which has the three reasons an inlined image breaks this product.
            DocumentFigure,
            // TAB STOPS. The document stores a tab as a CHARACTER with no width; where it lands is
            // recomputed from where it sits, at both ends, so editing the text before it moves it.
            // TabWidths does the measuring; the ruler places the stops. See TabStops.ts.
            Tab, TabWidths,
            // WHERE THE PAGES BREAK, drawn as decorations so ProseMirror does not reconcile them
            // away. See pagination.ts -- it is a preview and nothing it does enters the document.
            Pagination,
            PageBreak,
            // Headings too, not only paragraphs: a centred title is the commonest thing anybody
            // wants to align, and it is a heading.
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            // THE COMMENT ANCHOR. It carries a thread id and nothing else: the conversation lives
            // in the comment tables, because it has its own authors and times and outlives the
            // sentence it was about. The server strips these marks before hashing, so a comment
            // changes nothing about what an approval covers. See CommentMark.ts.
            // NUMBERED HEADINGS, drawn as decorations from the shared outline rule. Off unless
            // the document asks for it. See OutlineNumbers.ts.
            OutlineNumbers,
            // CROSS-REFERENCES AND THE CONTENTS LIST. Both store only what they point AT; the
            // words are worked out from the outline at display time, at both ends. Each has a
            // one-pass filler beside it, for the same reason the tab widths do: what a reference
            // reads as depends on the headings AROUND it, and a node view only knows when its own
            // node changed.
            HeadingAnchor, CrossReference, CrossReferenceText,
            // A NAMED PASSAGE, so a reference can point at something that is not a section. Signed
            // with the document, unlike a comment anchor -- it is what the references resolve
            // against. See BookmarkMark.ts.
            BookmarkAnchor,
            // FOOTNOTES. The marker is an atom carrying the note's words; the number is counted at
            // display time, like a heading's, because it is a fact about the document and not
            // about the note. See Footnote.ts and footnotes.ts.
            Footnote, FootnoteNumbers,
            TableOfContents, TableOfContentsText,
            CommentAnchor.configure({
                get: () => ({
                    active: activeThreadRef.current,
                    resolved: resolvedThreadsRef.current,
                }),
            }),
            Collaboration.configure({ document: doc }),
            CollaborationCaret.configure({
                provider: { awareness } as any,
                user: {
                    name: sessionContext?.getName?.() || "Someone",
                    color: CARET_COLOURS[doc.clientID % CARET_COLOURS.length],
                },
            }),
        ],
        // CREATED EDITABLE, THEN IMMEDIATELY SET TO THE SERVER'S ANSWER by the effect below.
        //
        // This is not a relaxation of the gate -- the effect runs on mount, before the body is even
        // fetched, and the real gate has always been the server. It is the only way to get column
        // resizing: TipTap's Table extension adds the resizing plugin only if `editor.isEditable`
        // AT THE MOMENT THE PLUGINS ARE BUILT, which is inside `new Editor()`. Created read-only,
        // the plugin is never added and never added later -- so the cursor never changed over a
        // border and no column could be dragged, in a document that was perfectly editable.
        editable: true,

        editorProps: {
            // A MARKER IS CLICKED TO BE READ, not only to be edited. The note's words live in an
            // attribute, so there is nowhere on the page they appear in full except the band at
            // the foot of the sheet -- and that band is only drawn when the page preview is on.
            // Clicking the marker is the one way to see a note that is always available.
            handleClickOn(view: any, _position: number, node: any, nodePosition: number) {
                if (node?.type?.name !== "footnote") return false;
                // COUNTED HERE RATHER THAN STORED, because that is the only place the number
                // exists: it is how many notes are ahead of this one, which nothing on the node
                // knows. Read off the view's own state -- `editor` is not in scope inside the
                // options that create it.
                let ahead = 0;
                view.state.doc.descendants((child: any, at: number) => {
                    if (child.type.name === "footnote" && at < nodePosition) ahead += 1;
                });
                setNoteEdit({ at: nodePosition, number: ahead + 1, text: node.attrs?.text || "" });
                return true;
            },
        },
    }, [doc]);


    // ---------------------------------------------------------------- menus

    /**
     * The rendition, fetched and handed to the browser.
     *
     * THE LINK IS OPENED, NOT FETCHED INTO MEMORY. The server answers with a presigned S3 URL and
     * the object carries its own Content-Disposition, so the browser downloads it straight from S3
     * -- the file never passes through the API, which is both the cheap way and the only way that
     * works for a long procedure.
     *
     * A NEW TAB RATHER THAN window.location, so a half-finished edit is not navigated away from.
     */
    const downloadPdf = async () => {
        setNotice("Rendering the PDF…");
        try {
            const made = await api.rendition?.(documentUSID);
            if (!made?.Url) throw new Error("no link came back");
            setNotice(null);
            window.open(made.Url, "_blank", "noopener");
        } catch (error: any) {
            setNotice(error?.response?.data?.Cause || "That document could not be rendered.");
        }
    };

    /**
     * The Word copy, by the same route as the PDF: built on the server, stored, and the browser
     * sent to its address. The file never passes through the API.
     */
    const downloadWord = async () => {
        setNotice("Building the Word copy…");
        try {
            const made = await api.wordExport?.(documentUSID);
            if (!made?.Url) throw new Error("no link came back");
            setNotice(null);
            window.open(made.Url, "_blank", "noopener");
        } catch (error: any) {
            setNotice(error?.response?.data?.Cause || "That document could not be exported.");
        }
    };

    const importDocx = async (file: File) => {
        setNotice(null);
        try {
            const buffer = await file.arrayBuffer();
            // Chunked, because spreading a megabyte of bytes into String.fromCharCode blows the
            // argument limit and throws on exactly the files worth importing.
            let binary = "";
            const bytes = new Uint8Array(buffer);
            for (let i = 0; i < bytes.length; i += 0x8000) {
                binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 0x8000)) as any);
            }
            const made = await api.importDocx?.(documentUSID, btoa(binary), file.name);
            setNotice(made?.NeedsReview
                // THE CONVERTER'S OWN VERDICT, not a cheerful "done". A .docx records that a line
                // is bold; it does not record that the author meant a heading. Where it had to
                // guess it says so, and the document is marked unreconciled until a person looks.
                ? `Imported ${file.name}. The converter had to interpret the structure in ${made.NeedsReview} place(s) — check the headings before this is submitted.`
                : `Imported ${file.name}.`);
            onImported?.();
        } catch (error: any) {
            setNotice(error?.response?.data?.Cause || "That file could not be imported.");
        }
    };

    /**
     * Put one or more pictures into the document.
     *
     * ONE AT A TIME, IN ORDER, deliberately. Three parallel multipart uploads contend for the same
     * connection and finish out of order, so the figures land in an order nobody chose -- and a
     * failure halfway through a batch leaves no way to say which ones got in.
     *
     * INSERTED ONLY AFTER THE UPLOAD FINISHES. A placeholder node inserted first and rewritten on
     * completion would be a node holding no attachment, which is exactly what every OTHER editor in
     * this session would receive over the CRDT and never be able to resolve.
     */
    const insertFigures = async (files: File[]) => {
        setNotice(null);

        // REFUSED BEFORE ANY OF THEM START. A batch where the fourth file is a 40MB TIFF should say
        // so before three uploads have run, not after.
        const refused = files.map(rejectionReason).filter(Boolean);
        if (refused.length) {
            setNotice(refused[0] as string);
            return;
        }

        for (const file of files) {
            try {
                setNotice(`Uploading ${file.name}…`);
                const figure = await api.uploadFigure?.(documentUSID, file);
                if (!figure) throw new Error("Uploading is not available in this window.");
                // THE URL IS REMEMBERED, NOT STORED. It goes in the module-level map the node views
                // read; writing it into the body would expire inside the document and move the
                // ContentDigest every time it was refreshed. See DocumentFigure.ts.
                rememberFigures({[figure.attachment]: {Url: figure.url}});
                editor?.chain().focus().insertFigure({
                    attachment: figure.attachment,
                    alt: file.name,
                    ...(figure.width ? {width: figure.width} : {}),
                }).run();
                setNotice(null);
            } catch (error: any) {
                setNotice(error?.response?.data?.Cause || error?.message
                          || `${file.name} could not be added.`);
                return;
            }
        }
    };

    /** What this document IS, in one line -- the question File ▸ Details answers everywhere else. */
    const details = () => {
        const text = editor?.getText?.() ?? "";
        const words = (text.match(/\S+/g) ?? []).length;
        return [
            `${words.toLocaleString()} words, ${text.length.toLocaleString()} characters.`,
            outstanding ? `${outstanding} tracked change(s) outstanding.` : "No tracked changes.",
            `${pages} page${pages === 1 ? "" : "s"}.`,
            digest.current ? `Content digest ${digest.current.slice(0, 12)}…` : "Not saved yet.",
        ].join(" ");
    };

    const words = () => {
        const text = editor?.getText?.() ?? "";
        const count = (text.match(/\S+/g) ?? []).length;
        setNotice(`${count.toLocaleString()} words, ${text.length.toLocaleString()} characters.`);
    };

    const chooseWidth = (wide: boolean) => {
        setFullWidth(wide);
        try { localStorage.setItem("docs.fullWidth", wide ? "1" : "0"); } catch { /* private */ }
    };

    /**
     * The approved vocabulary for this revision, fetched once.
     *
     * FAILS QUIET, DELIBERATELY. A glossary that cannot be read is a reading aid that is missing,
     * not a document that cannot be opened -- and a tenant with no Enterprise Glossary at all is
     * the normal state of a new installation, which must not look like an error.
     */
    useEffect(() => {
        const ask = api?.glossary;
        if (!revisionUSID || !ask) return;
        let cancelled = false;
        (async () => {
            try {
                const answer = await ask(revisionUSID);
                if (!cancelled) setGlossaryTerms(answer?.Terms ?? []);
            } catch {
                if (!cancelled) setGlossaryTerms([]);
            }
        })();
        return () => { cancelled = true; };
    }, [revisionUSID, api]);

    /** Hand the plugin the vocabulary and the reader's choice. Both go the same way, because to the
     *  plugin "there are no terms" and "do not show them" are the same empty page. */
    useEffect(() => {
        if (editor) setGlossary(editor, { terms: glossaryTerms, on: glossaryOn });
    }, [editor, glossaryTerms, glossaryOn]);

    const chooseGlossary = (on: boolean) => {
        setGlossaryOn(on);
        try { localStorage.setItem("docs.glossary", on ? "1" : "0"); } catch { /* private */ }
    };

    /**
     * Put an old version back, as a SAVE.
     *
     * NOT A SEPARATE PATH. It goes through the same api.save the editor uses, so t1 is asked
     * whether the revision may change, the digest fence applies, and the restore lands in the
     * history as an event of its own -- "Elena restored the 10:14 version" is a sentence the trail
     * can produce. A restore that wrote directly would be a way to change a controlled document
     * without any of the checks that make the others safe.
     *
     * THROUGH THE EDITOR, so the change reaches the other windows: setting the content dispatches a
     * transaction, the CRDT carries it, and the debounced save follows. Writing to the server
     * behind the editor's back would leave every open browser showing the old text.
     */
    const restore = (version: Version, body: any) => {
        if (!editor || !body) return;
        // The save that follows this dispatch is the restore, and says so.
        pendingSource.current = "RESTORE";
        editor.view.dispatch(
            editor.view.state.tr
                .setMeta("parkourSeeding", true)
                .replaceWith(0, editor.view.state.doc.content.size,
                             editor.schema.nodeFromJSON(body).content));
        setViewing(null);
        setViewingBody(null);
        setNotice(`Restored the version from ${new Date(version.At).toLocaleString()}.`);
    };

    const chain = () => editor?.chain().focus();

    // HOW MANY ARE OUTSTANDING, counted from the document rather than from the last server
    // response: this number decides whether Accept all is offered, and it has to be true the
    // instant somebody resolves one. The server counts the same thing on save and that is the
    // count the approval gate reads -- this one is only for the menus.
    const outstanding = editor ? allSuggestions(editor).length : 0;

    // WHETHER THE DOCUMENT CARRIES A FOOTER OF ITS OWN. Read from the document on every
    // transaction (the editor's `transaction` listener bumps this component), so inserting one
    // swaps the automatic footer for it in the same frame -- and so does a peer inserting one.
    const ownFooter = editor ? hasFooter(editor) : false;
    const ownHeader = editor ? hasHeader(editor) : false;
    const titleRow = editor ? hasTitleRow(editor) : false;
    const setup = editor ? pageSetup(editor) : undefined;
    const look = editor ? tableLook(editor) : null;

    // THE EDGE SOMEBODY HAS CLICKED, with what it currently is -- its own setting when it has one,
    // and what it inherits from the table when it does not, so a control shows the line that is
    // actually drawn rather than a default nobody chose.
    const pickedBorder = (() => {
        const picked = selectedBorder(editor);
        const value = selectedBorderValue(editor, look?.borderWidth ?? null, look?.borderColor ?? null);
        return picked && value
            ? { edge: picked.edge, width: value.width, colour: value.colour, dash: value.dash }
            : null;
    })();

    /**
     * Whether a command would actually do something, asked of the editor rather than guessed.
     *
     * EVERY TABLE ITEM WAS ENABLED EVERYWHERE, and clicking one with the caret in a paragraph did
     * nothing at all -- no table, no message, no sign that the menu had been read and dismissed.
     * ProseMirror already knows: `can()` runs the command against the current state without
     * applying it, which is the same answer the command itself would give.
     *
     * Wrapped in a try because `can()` on a command whose extension is absent throws rather than
     * answering false, and a menu must not take the page down with it.
     */
    const can = (command: string, ...args: any[]) => {
        try { return !!(editor?.can() as any)?.[command]?.(...args); } catch { return false; }
    };
    const inTable = !!look;
    const TABLE_REASON = "Put the caret in a table first.";

    // HOW MANY SHEETS THE DOCUMENT FILLS, reported back by the page after it has measured the
    // breaks. It is shown in File ▸ Details, where "how long is this" is already answered in words
    // and characters and was missing the only unit a printed procedure is actually counted in.
    const [pages, setPages] = useState(1);

    // MEASURED AFTER THE BROWSER HAS LAID THE TEXT OUT, on every render (one per transaction) and
    // again whenever the text changes height with no render to hang it on -- a web font arriving,
    // a table reflowing. measurePagination dispatches only when the breaks have actually moved, so
    // leaving the observer running cannot loop.
    const measuring = useRef(false);
    useEffect(() => {
        if (!editor) return;
        const run = () => setPages(measurePagination(editor, !fullWidth));
        // THE FRAME HAS TO BE CANCELLED. Without this the callback fires after the component has
        // gone -- on a reload, a navigation, or a switch between parts -- and measures an editor
        // that is being torn down. Harmless-looking, and it is how the unmount error above got to
        // be visible at all.
        let frame = 0;
        const schedule = () => {
            if (measuring.current) return;
            measuring.current = true;
            frame = requestAnimationFrame(() => { measuring.current = false; run(); });
        };
        schedule();
        // SAME GUARD AS measurePagination, for the same reason: this effect runs on renders that
        // happen before the view exists, and reading editor.view then throws.
        let dom: HTMLElement | null = null;
        try { dom = editor.view?.dom ?? null; } catch { dom = null; }
        const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
        if (dom) observer?.observe(dom);
        window.addEventListener("resize", schedule);
        return () => {
            // AND THE LATCH HAS TO BE LET GO WITH IT. `measuring` is set before the frame is
            // requested and cleared inside it; cancelling the frame without clearing it leaves the
            // latch stuck TRUE, and every later schedule() returns at the first line. Pagination
            // then stops for the life of the editor -- no breaks, no page count, no footnote bands
            // -- with nothing in the console and the text still on screen, which reads as the
            // feature never having worked rather than as a measurement that stopped being taken.
            if (frame) cancelAnimationFrame(frame);
            measuring.current = false;
            observer?.disconnect();
            window.removeEventListener("resize", schedule);
        };
    });

    // WHAT THE FIELDS SAY. The record is the only source: a field renders the number, revision
    // and state the record holds RIGHT NOW, which is what stops a footer from printing last
    // year's revision. Module-level rather than context because a node view is DOM, not React --
    // see PageElements.
    useEffect(() => {
        setDocumentFieldValues({
            objectId: record?.ObjectID ?? null,
            name: record?.Name ?? null,
            revision: record?.DisplayRevision ?? null,
            status: status ?? null,
            account: sessionContext?.getAccountName?.() ?? null,
        });
    }, [record?.ObjectID, record?.Name, record?.DisplayRevision, status, sessionContext]);

    // THE PIECES TWO MENUS SHARE. Insert and Format both offer the table operations, the page
    // elements and the two unbuilt things, exactly as Google's do -- and a submenu that differed
    // between them by one row would be a bug nobody would ever notice.
    // WHY A FIGURE IS NOT A base64 IMAGE, kept here because this is where somebody would be
    // tempted to add one: the body crosses a 6MB lambda, travels as a CRDT over a transport that
    // caps a message at 128KB, and is evidence whose files need the retention the attachment store
    // already has. So an image is an attachment and the body holds its USID. See DocumentFigure.ts.
    const IMAGE_REASON = api.uploadFigure
        ? undefined
        : "Uploading is not available in this window.";

    const FIGURE_SELECT_REASON =
        "Click the figure first — wrapping is a property of one picture, not of the document.";

    // WHICH FIGURE THE WRAP SETTING APPLIES TO: the one that is selected. A figure is an atom, so
    // selecting it is clicking it, and `isActive` answers for the node the selection holds.
    const figureWrap = (): string =>
        (editor?.getAttributes?.("image")?.wrap as string) || "none";
    const setFigureWrap = (wrap: "none" | "left" | "right") =>
        editor?.chain().focus().updateAttributes("image", { wrap }).run();
    const onFigure = !!editor?.isActive?.("image");

    const IMAGE_ACTIONS = [
        {label: "Upload from computer…", icon: "upload", unavailable: IMAGE_REASON,
         onClick: () => figureInput.current?.click()},
        // GOOGLE'S THREE, AND ITS WORDING. "In line" is what every figure in every document
        // already is; the other two put the picture against one margin and run the words down its
        // other side -- on screen with a float, in the PDF with ReportLab's own wrapping, which is
        // the same idea one layer down.
        {label: "Text wrapping", icon: "wrap_text", divider: true, submenu: [
            {label: "In line with text", icon: "format_align_justify",
             checked: figureWrap() === "none",
             onClick: editable && onFigure ? () => setFigureWrap("none") : undefined,
             unavailable: !editable ? "This revision is not open for editing."
                        : !onFigure ? FIGURE_SELECT_REASON : undefined},
            {label: "Wrap text — figure on the left", icon: "format_image_left",
             checked: figureWrap() === "left",
             onClick: editable && onFigure ? () => setFigureWrap("left") : undefined,
             unavailable: !editable ? "This revision is not open for editing."
                        : !onFigure ? FIGURE_SELECT_REASON : undefined},
            {label: "Wrap text — figure on the right", icon: "format_image_right",
             checked: figureWrap() === "right",
             onClick: editable && onFigure ? () => setFigureWrap("right") : undefined,
             unavailable: !editable ? "This revision is not open for editing."
                        : !onFigure ? FIGURE_SELECT_REASON : undefined},
        ]},
    ];
    // BORDERS ARE BUILT, AND THESE TWO ENTRIES SAID THEY WERE NOT. The same staleness File ▸ Page
    // setup had: each cell carries its own top, right, bottom and left -- width, colour and dash --
    // the Table options panel sets them, and document_rendition reads the same attributes to draw
    // the same lines. What is true is that a border belongs to an EDGE, so there is nothing to
    // apply until you have said which edge you mean, and that is what the panel is for.
    const BORDERS_REASON =
        "Click the edge you mean inside the table first — a border belongs to one side of one "
        + "cell, not to the table. Then set its weight, colour and dash in Table options.";
    // DISTRIBUTING COLUMNS IS BUILT -- the width per column IS stored (`colwidth`, on every cell,
    // which is where ProseMirror's table schema keeps it) and document_rendition reads it. This
    // said otherwise, like File ▸ Page setup and Format ▸ Borders & lines did.
    //
    // ROWS ARE A DIFFERENT MATTER and this is the half that is genuinely absent: a row height has
    // to be honoured by whatever paginates, and for the signed PDF that is the rendition, which
    // sizes a row from what is in it.
    // DISTRIBUTING ROWS IS NOT A MINIMUM HEIGHT, and conflating them would be the easy mistake:
    // "distribute" makes every row the same height whatever is in it, which in a controlled
    // document is a way to hide text. The minimum is the thing people actually want -- a signature
    // block tall enough to write in -- and that is in Table options.
    const TABLE_ROWS_REASON =
        "Rows are sized by what is in them. For a row that must be tall enough to write in — a "
        + "signature block — set its minimum height in Table options.";
    const PAGE_NUMBERS_REASON =
        "A page number needs pagination, and the browser cannot count pages: counter(page) works "
        + "only inside @page margin boxes, which no browser implements. Numbering comes from the "
        + "rendition — the server-side render that produces the PDF that gets signed — along with "
        + "the running header and footer. File ▸ Download ▸ PDF.";

    // GOOGLE'S FOUR, and the values a word processor has always meant by them.
    const LINE_SPACINGS: Array<[string, string]> = [
        ["Single", "1"], ["1.15", "1.15"], ["1.5", "1.5"], ["Double", "2"],
    ];

    // GOOGLE'S FORMAT ▸ TABLE SUBMENU, row for row and divider for divider. Note what is NOT
    // here: inserting a table. Google puts that under Insert ▸ Table, because this menu acts on the
    // table the caret is already in -- and an "Insert table" at the top of it was the first thing
    // that made ours read as an approximation rather than the same menu.
    const TABLE_ACTIONS: MenuAction[] = [
        {label: "Insert title row", icon: "table_rows",
         onClick: inTable ? () => insertTitleRow(editor) : undefined,
         unavailable: inTable ? undefined : TABLE_REASON},

        {label: "Insert row above", icon: "add_row_above", divider: true,
         onClick: can("addRowBefore") ? () => chain()?.addRowBefore().run() : undefined,
         unavailable: can("addRowBefore") ? undefined : TABLE_REASON},
        {label: "Insert row below", icon: "add_row_below",
         onClick: can("addRowAfter") ? () => chain()?.addRowAfter().run() : undefined,
         unavailable: can("addRowAfter") ? undefined : TABLE_REASON},
        {label: "Insert column left", icon: "add_column_left",
         onClick: can("addColumnBefore") ? () => chain()?.addColumnBefore().run() : undefined,
         unavailable: can("addColumnBefore") ? undefined : TABLE_REASON},
        {label: "Insert column right", icon: "add_column_right",
         onClick: can("addColumnAfter") ? () => chain()?.addColumnAfter().run() : undefined,
         unavailable: can("addColumnAfter") ? undefined : TABLE_REASON},

        {label: "Delete row", icon: "delete", divider: true,
         onClick: can("deleteRow") ? () => chain()?.deleteRow().run() : undefined,
         unavailable: can("deleteRow") ? undefined : TABLE_REASON},
        {label: "Delete column", icon: "delete",
         onClick: can("deleteColumn") ? () => chain()?.deleteColumn().run() : undefined,
         unavailable: can("deleteColumn") ? undefined : TABLE_REASON},
        {label: "Delete table", icon: "delete",
         onClick: can("deleteTable") ? () => chain()?.deleteTable().run() : undefined,
         unavailable: can("deleteTable") ? undefined : TABLE_REASON},

        // PINNING A HEADER IS NOT DECORATION HERE. The rendition repeats a header row at the top of
        // every page it spills onto (repeatRows), so this is the control for that -- which is the
        // same thing Google's wording means.
        // THE REASON IS ONLY SET WHEN THERE IS ONE. A non-empty `unavailable` is what disables a
        // row, so writing it as `!inTable ? REASON : "already has a title row"` disabled the item
        // in BOTH cases -- including the one where it works. Every pair below reads the same way
        // now: the action, then the reason it is not offered, and nothing when it is.
        {label: "Pin header up to this row", icon: "push_pin", divider: true,
         onClick: inTable && !titleRow ? () => chain()?.toggleHeaderRow().run() : undefined,
         unavailable: !inTable ? TABLE_REASON
                    : titleRow ? "This table already has a title row." : undefined},
        {label: "Unpin header row(s)", icon: "keep_off",
         onClick: inTable && titleRow ? () => chain()?.toggleHeaderRow().run() : undefined,
         unavailable: !inTable ? TABLE_REASON
                    : !titleRow ? "This table has no title row." : undefined},

        {label: "Distribute rows", icon: "format_line_spacing", divider: true,
         unavailable: TABLE_ROWS_REASON},
        // EQUAL SHARES OF THE TEXT COLUMN, which is what "distribute" means in every word
        // processor: the table keeps the width it has and its columns stop being ragged. It is the
        // quick layout the Table options panel already offers, reached from where people look for
        // it -- one implementation, so the two cannot disagree.
        {label: "Distribute columns", icon: "view_column",
         // ITS OWN ALIGNMENT IS KEPT. applyQuickLayout writes the table's placement as well as its
         // widths, so passing a fixed "left" would quietly shove a centred table to the margin.
         onClick: inTable ? () => applyQuickLayout(editor, 1,
             (tableAt(editor)?.node?.attrs?.align as any) || "left") : undefined,
         unavailable: inTable ? undefined : TABLE_REASON},

        // MERGE AND UNMERGE ARE NOT "AM I IN A TABLE". Merging needs CELLS SELECTED -- a caret in
        // one cell has nothing to merge with -- and unmerging needs a cell that was merged. `can()`
        // answers both exactly, which a hand-written rule would get wrong the first time somebody
        // selected across a row.
        {label: "Merge cells", icon: "cell_merge", divider: true,
         onClick: can("mergeCells") ? () => chain()?.mergeCells().run() : undefined,
         unavailable: can("mergeCells") ? undefined
                    : !inTable ? TABLE_REASON : "Select the cells to merge first."},
        {label: "Unmerge cells", icon: "splitscreen",
         onClick: can("splitCell") ? () => chain()?.splitCell().run() : undefined,
         unavailable: can("splitCell") ? undefined
                    : !inTable ? TABLE_REASON : "This cell is not merged."},

        {label: "Split cell", icon: "splitscreen_vertical_add", divider: true,
         onClick: can("splitCell") ? () => chain()?.splitCell().run() : undefined,
         unavailable: can("splitCell") ? undefined
                    : !inTable ? TABLE_REASON
                    : "This cell is not merged, so there is nothing to split."},

        // SORTING IS AN EDIT TO SIGNED TEXT, which is why it waited and why the save that follows
        // carries Source "SORT": the audit row reads SORTED and the server keeps a snapshot of the
        // table as it stood. Without that the trail shows a hundred moved cells and no account of
        // why they moved. See sortRows().
        {label: "Sort rows A\u2013Z by this column", icon: "arrow_upward", divider: true,
         onClick: (editable && !sortableReason(editor))
             ? () => { if (sortRows(editor, "asc")) pendingSource.current = "SORT"; }
             : undefined,
         unavailable: !editable ? "This revision is not open for editing."
                                : (sortableReason(editor) ?? undefined)},
        {label: "Sort rows Z\u2013A by this column", icon: "arrow_downward",
         onClick: (editable && !sortableReason(editor))
             ? () => { if (sortRows(editor, "desc")) pendingSource.current = "SORT"; }
             : undefined,
         unavailable: !editable ? "This revision is not open for editing."
                                : (sortableReason(editor) ?? undefined)},
        // THE MENU OPENS THE PANEL rather than carrying its own submenu of types: a choice
        // column needs its list of permitted values typed in, which is a panel's job.
        {label: "Set column type", icon: "category",
         onClick: (inTable && editable) ? () => setTableOptions(true) : undefined,
         unavailable: !inTable ? TABLE_REASON
                     : !editable ? "This revision is not open for editing." : undefined},

        {label: "Table options", icon: "tune", divider: true,
         onClick: inTable ? () => setTableOptions(true) : undefined,
         unavailable: inTable ? undefined : TABLE_REASON},
    ];

    // INSERT ▸ TABLE IS WHERE A TABLE IS MADE, as Google has it -- a size, not a list of operations.
    const TABLE_SIZES: MenuAction[] = [2, 3, 4, 5].map(size => ({
        label: `${size} × ${size}`, icon: "table",
        onClick: can("insertTable", {rows: size, cols: size, withHeaderRow: true})
            ? () => chain()?.insertTable({rows: size, cols: size, withHeaderRow: true}).run()
            : undefined,
        unavailable: can("insertTable", {rows: size, cols: size, withHeaderRow: true})
            ? undefined : "A table cannot go here.",
    }));

    const DOCUMENT_FIELDS: MenuAction[] =
        (Object.keys(FIELD_LABELS) as Array<keyof typeof FIELD_LABELS>).map((name) => ({
            label: FIELD_LABELS[name], icon: "label",
            onClick: editable ? () => insertField(editor, name) : undefined,
            unavailable: editable ? undefined : "This revision is not open for editing.",
        }));

    // INSERT ▸ PAGE ELEMENTS, in Google's order: Table of contents, Header, Footer, Watermark,
    // Page numbers, Footnote. This is where a page element is MADE -- Header and Footer create one
    // if there is none and otherwise take you to it, because "is there a footer yet" is not a
    // question an author should have to answer to reach it.
    const PAGE_ELEMENTS: MenuAction[] = [
        // A CONTENTS LIST THAT IS THE DOCUMENT'S SHAPE, not a copy of it: nothing is stored but
        // the fact that it is there, so there is no "update" button and nothing to go stale.
        {label: "Table of contents", icon: "toc",
         onClick: editable
             ? () => editor?.chain().focus().insertContent({ type: "tableOfContents" }).run()
             : undefined,
         unavailable: editable ? undefined : "This revision is not open for editing."},
        {label: ownHeader ? "Header — go to it" : "Header", icon: "border_top", divider: true,
         onClick: editable ? () => insertHeader(editor) : undefined,
         unavailable: editable ? undefined : "This revision is not open for editing."},
        {label: ownFooter ? "Footer — go to it" : "Footer", icon: "border_bottom",
         onClick: editable ? () => insertFooter(editor) : undefined,
         unavailable: editable ? undefined : "This revision is not open for editing."},
        // NOT A COMMAND, AND NOT MISSING EITHER. The watermark is derived from the revision's
        // state and drawn on every page by both the editor and the rendition, so there is nothing
        // to insert and nothing to switch off -- which is the point: a draft that could be printed
        // without its mark is the hazard the mark exists for. The entry stays so that somebody
        // looking for it finds the answer instead of finding nothing.
        {label: "Watermark", icon: "water_drop", unavailable:
            watermarkFor(status)
                ? `Automatic. Every page of this revision is marked ${watermarkFor(status)}, on `
                  + "screen and in the PDF, because that is its state. It is not typed and cannot "
                  + "be removed -- a draft printed without its mark is the thing a watermark is for."
                : "Automatic. A released revision carries no watermark: it IS the controlled "
                  + "version. Every other state is marked across every page, on screen and in the "
                  + "PDF, and the mark is the state rather than anything anybody types."},
        {label: "Page numbers", icon: "tag", unavailable: PAGE_NUMBERS_REASON},
    ];

    // FORMAT ▸ HEADERS & FOOTERS is Google's two rows: what you can take away, and the settings.
    // Creating one lives under Insert ▸ Page elements, which is where Google puts it too -- this
    // menu is about the header you already have.
    const HEADERS_AND_FOOTERS: MenuAction[] = [
        {label: "Remove header", icon: "delete",
         onClick: editable && ownHeader ? () => removeHeader(editor) : undefined,
         unavailable: !editable ? "This revision is not open for editing."
                    : !ownHeader ? "This document has no header. Insert ▸ Page elements ▸ Header."
                    : undefined},
        {label: "Remove footer", icon: "delete",
         onClick: editable && ownFooter ? () => removeFooter(editor) : undefined,
         unavailable: !editable ? "This revision is not open for editing."
                    : !ownFooter ? "This document has no footer of its own — the one on the page "
                                 + "is drawn from the QMS record and cannot be removed."
                    : undefined},
        {label: "More options", divider: true,
         onClick: editable ? () => setPageOptions(true) : undefined,
         unavailable: editable ? undefined : "This revision is not open for editing."},
    ];

    // ------------------------------------------------------------- named styles

    /** The style the caret is sitting in. */
    const styleHere = (): StyleName => {
        if (!editor) return "normal";
        const node = editor.state.selection.$from.parent;
        return styleFor(node?.type?.name, node?.attrs?.level);
    };

    /**
     * Take the direct formatting off the selected blocks, so the style is what you see.
     *
     * APPLYING A STYLE HAS TO CLEAR, or the feature does not work: a heading somebody once made 14
     * point stays 14 point whatever the style says, and "update every one of them to match" quietly
     * misses exactly the paragraphs somebody has been fiddling with. Direct formatting still wins
     * when you put it back deliberately -- this is the one command that takes it away.
     */
    const clearDirect = () => {
        if (!editor) return;
        editor.chain().focus()
              .unsetFontSize().unsetBold().unsetItalic().unsetColor()
              .setLineHeight(null as any)
              .run();
        setParagraphSpace(editor, "spaceBefore", null);
        setParagraphSpace(editor, "spaceAfter", null);
    };

    const applyStyle = (name: StyleName) => {
        if (!editor) return;
        if (name === "normal") editor.chain().focus().setParagraph().run();
        // `as any` ON THE LEVEL ALONE: TipTap types it as a union of the six it was configured
        // with, and the number comes out of a style name that is only ever h1..h6 -- the names are
        // the enumeration, so narrowing it again here would be a second list to keep in step.
        else editor.chain().focus().setHeading({ level: Number(name.slice(1)) as any }).run();
        clearDirect();
    };

    /**
     * "Update Heading 2 to match" — the headline command, and the reason the definition lives on
     * the document rather than on each paragraph.
     *
     * READ FROM THE BLOCK, NOT FROM THE SCREEN. Measuring the rendered element would read back the
     * style that is already applied, so every update would be a no-op that looked like a bug. What
     * is captured is the DIRECT formatting on the paragraph the caret is in.
     *
     * AND THE BLOCK IS CLEARED AFTERWARDS. Its direct formatting and the style now say the same
     * thing, so nothing changes on screen -- but leaving it would make this one paragraph immune to
     * the next change to the style it just defined, which is the surprise nobody recovers from.
     */
    const updateStyleToMatch = (name: StyleName) => {
        if (!editor) return;
        const attributes = editor.getAttributes("textStyle") || {};
        const block = editor.state.selection.$from.parent;
        const size = parseFloat(String(attributes.fontSize || "").replace("pt", ""));
        const next = {
            ...stylesOf(editor.state.doc.attrs?.paragraphStyles),
            [name]: captured(editor.state.doc.attrs?.paragraphStyles, name, {
                size: Number.isFinite(size) ? size : null,
                bold: editor.isActive("bold") ? true : null,
                italic: editor.isActive("italic") ? true : null,
                colour: attributes.color || null,
                spaceBefore: block?.attrs?.spaceBefore ?? null,
                spaceAfter: block?.attrs?.spaceAfter ?? null,
                lineHeight: parseFloat(String(block?.attrs?.lineHeight ?? "")) || null,
            }),
        };
        setParagraphStyles(editor, next);
        clearDirect();
        setNotice(`${STYLE_LABELS[name]} now matches this paragraph, everywhere in the document.`);
    };

    const resetStyle = (name: StyleName | null) => {
        if (!editor) return;
        if (name === null) { setParagraphStyles(editor, null); return; }
        const next: any = { ...stylesOf(editor.state.doc.attrs?.paragraphStyles) };
        delete next[name];
        setParagraphStyles(editor, next);
    };

    const menus: MenuDefinition[] = [
        {name: "File", items: [
            {label: "New document", icon: "note_add", onClick: onNewDocument},
            // FETCHED WHEN THE GALLERY IS OPENED, not when the editor loads. It is a read across
            // every revision in the tenant for a dialog most sessions never open; paying for it on
            // every document somebody opens would be the wrong way round.
            {label: "New from template", icon: "dashboard_customize",
             onClick: onNewFromTemplate ? async () => {
                 setTemplatesOpen(true);
                 setTemplatesLoading(true);
                 try {
                     const got = await api.templates?.();
                     setTemplates(got?.Templates ?? []);
                 } catch { setTemplates([]); }
                 setTemplatesLoading(false);
             } : undefined,
             unavailable: onNewFromTemplate ? undefined
                 : "Templates are started from a record, and this view has none."},
            // BESIDE "NEW FROM TEMPLATE", because it is the same act with a wider source: start
            // from something that already exists rather than from a blank page. The list it opens
            // is fetched by the page, for the same reason the gallery's is fetched on open.
            {label: "Create from existing document", icon: "file_copy",
             onClick: onNewFromExisting,
             unavailable: onNewFromExisting ? undefined
                 : "Documents are started from a record, and this view has none."},
            {label: "Open all documents", icon: "folder_open", shortcut: "⌘O", onClick: onOpenList},
            {label: "Make a copy", icon: "content_copy", onClick: onDuplicate,
             unavailable: editable ? undefined : "This revision is not open for editing."},
            {label: "Import Word document…", icon: "upload_file", divider: true,
             onClick: editable ? () => importInput.current?.click() : undefined,
             unavailable: editable ? undefined : "This revision is not open for editing."},
            // A SUBMENU, AS GOOGLE HAS, even though both children are unbuilt: the shape says
            // "this product exports documents, not yet in these formats", where two greyed rows
            // at the top level say "this product cannot export".
            {label: "Download", icon: "download", submenu: [
                {label: "PDF (.pdf)", icon: "picture_as_pdf", onClick: downloadPdf},
                // A CONVENIENCE COPY, AND IT SAYS SO ON EVERY PAGE. The rendition is the signed
                // artifact; a .docx leaves here and can be edited by anybody, so the file carries
                // UNCONTROLLED COPY with the number, revision and state.
                {label: "Microsoft Word (.docx)", icon: "description", onClick: downloadWord},
            ]},
            {label: "Rename", icon: "drive_file_rename_outline", divider: true, onClick: onRename},
            // A TEMPLATE IS STILL AN ORDINARY DOCUMENT, and the wording says so: this adds it to
            // the gallery, it does not change what it is. Nothing about its revision, its approval
            // or who may read it moves.
            {label: isTemplate ? "Remove from templates" : "Save as template",
             icon: isTemplate ? "bookmark_remove" : "bookmark_add",
             checked: !!isTemplate,
             onClick: editable && onSetTemplate ? () => onSetTemplate(!isTemplate) : undefined,
             unavailable: !editable ? "This revision is not open for editing."
                        : !onSetTemplate ? "This view cannot change that." : undefined},
            {label: "Move to trash", icon: "delete", onClick: onDelete,
             unavailable: deleteUnavailable},
            {label: "Version history", icon: "history", divider: true,
             checked: history,
             onClick: () => { setHistory(h => !h); setViewing(null); setViewingBody(null); }},
            {label: "Details", icon: "info", divider: true, onClick: () => setNotice(details())},
            {label: "Page setup", icon: "straighten",
             onClick: editable ? () => setPageOptions(true) : undefined,
             unavailable: editable ? undefined : "This revision is not open for editing."},
            {label: "Print", icon: "print", shortcut: "⌘P", onClick: () => window.print()},
        ]},
        {name: "Edit", items: [
            {label: "Undo", icon: "undo", shortcut: "⌘Z", onClick: () => chain()?.undo().run()},
            {label: "Redo", icon: "redo", shortcut: "⇧⌘Z", onClick: () => chain()?.redo().run()},
            {label: "Select all", icon: "select_all", shortcut: "⌘A", divider: true,
             onClick: () => chain()?.selectAll().run()},
            {label: "Find and replace", icon: "search", shortcut: "⌘F", onClick: () => setFinding(true)},
            // CUT, COPY AND PASTE ARE THE BROWSER'S and cannot be driven from a menu: a script
            // cannot read or write the clipboard without a user gesture aimed at it. Listing them
            // as dead entries would be three permanent disappointments in the commonest menu.
        ]},
        {name: "View", items: [
            {label: "Page width", icon: "fit_page", checked: !fullWidth, onClick: () => chooseWidth(false)},
            // AND GOING THE OTHER WAY IS ALLOWED, but it is said out loud. Full width is a
            // personal view setting held in this browser; the numbering is a property of the
            // document that everybody shares, so blocking the first because of the second would be
            // the wrong way round. What must not happen quietly is the screen and the signed PDF
            // numbering differently, so this says which one you are looking at.
            {label: "Full width", icon: "fit_screen", checked: fullWidth, onClick: () => {
                chooseWidth(true);
                if (editor?.state?.doc?.attrs?.footnoteNumbering === "page") {
                    setNotice("This document numbers its footnotes per page. At full width there "
                              + "are no pages, so they run straight through on screen — the PDF "
                              + "still numbers them per page.");
                }
            }},
            {label: "Print layout", icon: "description", checked: !fullWidth, divider: true,
             onClick: () => chooseWidth(false)},
            // THE OUTLINE IS A VIEW OF THE DOCUMENT, not an insertion into it -- which is why it
            // sits here and the table of contents sits under Insert. One is chrome, the other
            // prints.
            {label: "Show outline", icon: "toc", checked: outline,
             onClick: () => setOutline(v => !v)},
            // A VIEW OF THE DOCUMENT, like the outline above it -- the marks are decorations and
            // change nothing in the text. Says what is missing rather than going quiet: a tenant
            // with no released terms would otherwise get a control that appears to do nothing.
            {label: "Glossary terms", icon: "spellcheck", checked: glossaryOn,
             onClick: () => {
                 chooseGlossary(!glossaryOn);
                 if (!glossaryOn && !glossaryTerms.length) {
                     setNotice("No released glossary terms are visible to you, so nothing will be "
                               + "marked. Terms come from the Enterprise Glossary once they are "
                               + "released.");
                 }
             }},
        ]},
        // GOOGLE'S ORDER, GOOGLE'S GROUPING. Insert is the menu people reach into without
        // reading, so where a thing sits matters more here than in any other menu: Image, Table,
        // chips, Link -- then a rule, then the page furniture under Page elements, then Comment.
        {name: "Insert", items: [
            {label: "Image", icon: "image", submenu: IMAGE_ACTIONS},
            {label: "Table", icon: "table", submenu: TABLE_SIZES},
            // WHERE GOOGLE PUTS SMART CHIPS, because that is what these are: inline atoms that
            // render live data rather than text somebody typed. Ours are the facts QMS owns.
            {label: "Document fields", icon: "data_object", submenu: DOCUMENT_FIELDS},
            {label: "Link…", icon: "link", shortcut: "⌘K", onClick: () => {
                // eslint-disable-next-line no-alert
                const href = window.prompt("Link to", editor?.getAttributes("link").href ?? "");
                if (href === null) return;
                if (href === "") chain()?.unsetLink().run(); else chain()?.setLink({href}).run();
            }},
            {label: "Horizontal line", icon: "horizontal_rule",
             onClick: () => chain()?.setHorizontalRule().run()},
            // NEXT TO THE HORIZONTAL LINE, because that is the other thing people reach for when
            // they want "start the next bit somewhere else" -- and reaching for the wrong one is
            // how a procedure ends up with a rule where a break was meant.
            {label: "Page break", icon: "insert_page_break", divider: true,
             onClick: editable ? () => chain()?.insertPageBreak().run() : undefined,
             unavailable: editable ? undefined : "This revision is not open for editing."},
            {label: "Page elements", icon: "bottom_panel_open", submenu: PAGE_ELEMENTS},
            // A COMMENT IS OFFERED EVEN WHEN THE DOCUMENT CANNOT BE EDITED. That is the point:
            // a revision out for approval is exactly when somebody wants to ask about it, and the
            // anchor is stripped before the digest is taken so asking changes nothing that was
            // signed. What it does need is some text to be about.
            // "AS DESCRIBED IN 4.2", still correct after somebody inserts a section above it.
            // Beside Link, which is the other thing here that points somewhere -- not under Page
            // elements, which is headers, footers and watermarks.
            // THE OTHER HALF OF POINTING AT SOMETHING: this names the place, Cross-reference
            // points at it. A procedure refers to tolerances and warnings as often as to sections,
            // and turning those into headings to make them referenceable would wreck the
            // structure to get a pointer.
            {label: "Bookmark", icon: "bookmark_border",
             onClick: editable ? () => {
                 if (!editor || editor.state.selection.empty) return;
                 const { from, to } = editor.state.selection;
                 const suggested = editor.state.doc.textBetween(from, to, " ").slice(0, 60);
                 const name = window.prompt("Name this passage", suggested);
                 if (!name || !name.trim()) return;
                 editor.chain().focus()
                       .setMark("bookmark", { id: newBookmarkId(), name: name.trim() }).run();
             } : undefined,
             unavailable: !editable ? "This revision is not open for editing."
                 : editor?.state?.selection?.empty ? "Select the passage you want to name first."
                 : undefined},
            // A FOOTNOTE IS NOT A PAGE ELEMENT, whatever the fact that it lands at the foot of
            // a page suggests. A header belongs to the page and repeats on all of them; a footnote
            // belongs to a SENTENCE and goes where that sentence goes. It sits here, beside the
            // cross-reference and the comment -- the other two things you attach to a passage --
            // which is also where Google puts it and where it was looked for first.
            //
            // THE NUMBER IT WILL CARRY IS COUNTED BEFORE THE DIALOG OPENS, so the author is told
            // what the reader will see. Nothing is inserted until the note is written: a marker
            // with no words is a dangling promise in the sentence, and cancelling has to leave the
            // document exactly as it was.
            {label: "Footnote", icon: "superscript",
             onClick: editable ? () => {
                 if (!editor) return;
                 const to = editor.state.selection.from;
                 let ahead = 0;
                 editor.state.doc.descendants((child: any, at: number) => {
                     if (child.type.name === "footnote" && at < to) ahead += 1;
                 });
                 setNoteEdit({ at: null, number: ahead + 1, text: "" });
             } : undefined,
             unavailable: editable ? undefined : "This revision is not open for editing."},
            {label: "Cross-reference", icon: "link",
             onClick: editable ? () => setReferring(true) : undefined,
             unavailable: editable ? undefined : "This revision is not open for editing."},
            {label: "Comment", icon: "add_comment", shortcut: "⌘⌥M", divider: true,
             onClick: () => void addComment(),
             unavailable: editor?.state?.selection?.empty
                 ? "Select the text you want to ask about first." : undefined},
        ]},
        // GOOGLE'S FORMAT MENU, group for group: Text, Paragraph styles, Align & indent, Line &
        // paragraph spacing, Columns, Bullets & numbering -- then the page furniture, then Table,
        // then Clear formatting. Twelve rows at the top level became six submenus, which is the
        // point of the arrangement: you choose a KIND of formatting first and the specific thing
        // second, so the menu is the same length whatever we add to it.
        //
        // HEADERS & FOOTERS APPEARS HERE AS WELL AS UNDER INSERT, exactly as Google has it. It is
        // the one duplication worth having: inserting a footer and editing the one you already
        // have are the same command, and people look for the second under Format.
        {name: "Format", items: [
            // A DOCUMENT-WIDE FORMATTING CHOICE, which is why it is here and not under Insert:
            // nothing is inserted, the headings that already exist start carrying numbers.
            {label: "Numbered headings", icon: "format_list_numbered", divider: true,
             checked: !!editor?.state?.doc?.attrs?.outlineNumbering,
             onClick: () => {
                 if (!editor) return;
                 // `tr.setDocAttribute` does NOT exist in prosemirror-state 1.4.4 -- it would have
                 // type-checked, because the editor is `any`, and thrown at the click. setPageSetup
                 // already writes a document attribute properly, with a DocAttrStep.
                 setPageSetup(editor, {
                     outlineNumbering: !editor.state.doc.attrs?.outlineNumbering,
                 } as any);
             },
             unavailable: editable ? undefined : "This revision is not open for editing."},
            {label: "Text", icon: "format_bold", submenu: [
                {label: "Bold", icon: "format_bold", shortcut: "⌘B", checked: editor?.isActive("bold"),
                 onClick: () => chain()?.toggleBold().run()},
                {label: "Italic", icon: "format_italic", shortcut: "⌘I", checked: editor?.isActive("italic"),
                 onClick: () => chain()?.toggleItalic().run()},
                {label: "Underline", icon: "format_underlined", shortcut: "⌘U", checked: editor?.isActive("underline"),
                 onClick: () => chain()?.toggleUnderline().run()},
                {label: "Strikethrough", icon: "strikethrough_s", shortcut: "⌘⇧X", checked: editor?.isActive("strike"),
                 onClick: () => chain()?.toggleStrike().run()},
                {label: "Superscript", icon: "superscript", divider: true, checked: editor?.isActive("superscript"),
                 onClick: () => chain()?.toggleMark("superscript").run()},
                {label: "Subscript", icon: "subscript", checked: editor?.isActive("subscript"),
                 onClick: () => chain()?.toggleMark("subscript").run()},
                {label: "Size", icon: "format_size", divider: true, submenu: SIZES.map(size => ({
                    label: `${size} pt`, checked: editor?.getAttributes("textStyle")?.fontSize === `${size}pt`,
                    onClick: () => chain()?.setFontSize(`${size}pt`).run(),
                }))},
                {label: "Capitalization", icon: "match_case", submenu: [
                    {label: "lowercase", onClick: () => setCase(editor, "lower")},
                    {label: "UPPERCASE", onClick: () => setCase(editor, "upper")},
                    {label: "Title Case", onClick: () => setCase(editor, "title")},
                ]},
            ]},
            // GOOGLE'S THREE LEVELS, and the third one is the feature. Choosing a style and
            // REDEFINING it are different acts that belong to the same name, so each style carries
            // its own short menu rather than the two being separate lists that can disagree about
            // which styles exist.
            // BESIDE THE HEADING NUMBERING, because they are the same kind of choice: a
            // document-wide numbering convention that travels with the procedure rather than with
            // whoever opens it. A house that numbers notes per section and one that numbers
            // straight through are both ordinary.
            {label: "Footnote numbering", icon: "superscript", divider: true, submenu: [
                {label: "Straight through the document", icon: "check",
                 checked: editor?.state?.doc?.attrs?.footnoteNumbering !== "section",
                 onClick: editable ? () => setPageSetup(editor,
                     { footnoteNumbering: "continuous" } as any) : undefined,
                 unavailable: editable ? undefined : "This revision is not open for editing."},
                {label: "Restart at each section", icon: "restart_alt",
                 checked: editor?.state?.doc?.attrs?.footnoteNumbering === "section",
                 onClick: editable ? () => setPageSetup(editor,
                     { footnoteNumbering: "section" } as any) : undefined,
                 unavailable: editable
                     ? undefined : "This revision is not open for editing."},
                // THE ONE THAT NEEDS A PAGE TO EXIST. Which section a note is in can be read off
                // the document; which page it is on is a property of the layout, so this follows
                // the pagination measurement.
                //
                // CHOOSING IT TURNS THE PREVIEW BACK ON rather than refusing. The entry used to be
                // disabled at full width, which was honest and useless: it told somebody what they
                // could not do and left them to work out that the fix was in a different menu. A
                // document-wide numbering choice is the stronger statement of intent, so the view
                // setting gives way to it.
                {label: "Restart on each page", icon: "insert_page_break",
                 checked: editor?.state?.doc?.attrs?.footnoteNumbering === "page",
                 onClick: editable ? () => {
                     if (fullWidth) chooseWidth(false);
                     setPageSetup(editor, { footnoteNumbering: "page" } as any);
                 } : undefined,
                 unavailable: editable ? undefined : "This revision is not open for editing."},
            ]},
            {label: "Paragraph styles", icon: "format_paragraph", submenu: [
                ...STYLE_NAMES.map((name, index) => ({
                    label: STYLE_LABELS[name],
                    icon: name === "normal" ? "format_paragraph" : `format_h${name.slice(1)}`,
                    checked: styleHere() === name,
                    submenu: [
                        {label: `Apply ${STYLE_LABELS[name]}`, icon: "check",
                         onClick: editable ? () => applyStyle(name) : undefined,
                         unavailable: editable ? undefined
                                               : "This revision is not open for editing."},
                        // THE HEADLINE COMMAND. It takes the formatting of the paragraph the caret
                        // is in and makes it the definition, so every paragraph of this style
                        // changes at once -- which is the whole point of a style having a name.
                        {label: `Update ${STYLE_LABELS[name]} to match`, icon: "sync",
                         onClick: editable ? () => updateStyleToMatch(name) : undefined,
                         unavailable: !editable ? "This revision is not open for editing."
                            : styleHere() !== name
                                ? `Put the caret in a ${STYLE_LABELS[name].toLowerCase()} first — `
                                  + "this takes that paragraph's formatting and makes it the style."
                                : undefined},
                        {label: `Reset ${STYLE_LABELS[name]}`, icon: "restart_alt", divider: true,
                         onClick: editable ? () => resetStyle(name) : undefined,
                         unavailable: !editable ? "This revision is not open for editing."
                            : isDefault(editor?.state?.doc?.attrs?.paragraphStyles, name)
                                ? `${STYLE_LABELS[name]} has not been changed in this document.`
                                : undefined},
                    ],
                    divider: index === 0,
                })),
                {label: "Reset all styles", icon: "restart_alt", divider: true,
                 onClick: editable ? () => resetStyle(null) : undefined,
                 unavailable: !editable ? "This revision is not open for editing."
                    : STYLE_NAMES.every(one => isDefault(editor?.state?.doc?.attrs?.paragraphStyles, one))
                        ? "No style has been changed in this document."
                        : undefined},
            ]},
            {label: "Align & indent", icon: "format_align_left", submenu: [
                {label: "Left", icon: "format_align_left", shortcut: "⌘⇧L", checked: editor?.isActive({textAlign: "left"}),
                 onClick: () => chain()?.setTextAlign("left").run()},
                {label: "Center", icon: "format_align_center", shortcut: "⌘⇧E", checked: editor?.isActive({textAlign: "center"}),
                 onClick: () => chain()?.setTextAlign("center").run()},
                {label: "Right", icon: "format_align_right", shortcut: "⌘⇧R", checked: editor?.isActive({textAlign: "right"}),
                 onClick: () => chain()?.setTextAlign("right").run()},
                {label: "Justified", icon: "format_align_justify", shortcut: "⌘⇧J", checked: editor?.isActive({textAlign: "justify"}),
                 onClick: () => chain()?.setTextAlign("justify").run()},
                {label: "Increase indent", icon: "format_indent_increase", shortcut: "⌘]", divider: true,
                 onClick: () => indentBlocks(editor, 1)},
                {label: "Decrease indent", icon: "format_indent_decrease", shortcut: "⌘[",
                 onClick: () => indentBlocks(editor, -1)},
            ]},
            {label: "Line & paragraph spacing", icon: "format_line_spacing", submenu: [
                ...LINE_SPACINGS.map(([label, value]) => ({
                    label, checked: editor?.getAttributes("paragraph")?.lineHeight === value,
                    onClick: () => chain()?.setLineHeight(value).run(),
                })),
                {label: "Add space before paragraph", icon: "vertical_align_top", divider: true,
                 onClick: () => setParagraphSpace(editor, "spaceBefore", 10)},
                {label: "Add space after paragraph", icon: "vertical_align_bottom",
                 onClick: () => setParagraphSpace(editor, "spaceAfter", 10)},
                {label: "Remove added space", icon: "format_clear",
                 onClick: () => { setParagraphSpace(editor, "spaceBefore", null);
                                  setParagraphSpace(editor, "spaceAfter", null); }},
            ]},
            // COLUMNS LIVE IN PAGE SETUP, with orientation and margins, because that is what they
            // are: a property of the page the text is set on, not a format applied to a selection.
            // The menu entry opens that dialog rather than growing a second place to set it.
            {label: "Columns\u2026", icon: "view_column",
             onClick: editable ? () => setPageOptions(true) : undefined,
             unavailable: editable ? undefined : "This revision is not open for editing."},
            {label: "Bullets & numbering", icon: "format_list_bulleted", submenu: [
                {label: "Bulleted list", icon: "format_list_bulleted", checked: editor?.isActive("bulletList"),
                 onClick: () => chain()?.toggleBulletList().run()},
                {label: "Numbered list", icon: "format_list_numbered", checked: editor?.isActive("orderedList"),
                 onClick: () => chain()?.toggleOrderedList().run()},
                {label: "Checklist", icon: "checklist", checked: editor?.isActive("taskList"),
                 onClick: () => chain()?.toggleTaskList().run()},
                {label: "Quote", icon: "format_quote", divider: true, checked: editor?.isActive("blockquote"),
                 onClick: () => chain()?.toggleBlockquote().run()},
            ]},
            {label: "Headers & footers", icon: "bottom_panel_open", divider: true,
             submenu: HEADERS_AND_FOOTERS},
            {label: "Page numbers", icon: "tag", unavailable: PAGE_NUMBERS_REASON},
            // LANDSCAPE IS ONE SETTING IN ONE DIALOG, with the margins, because the page, the
            // print stylesheet and the server-side render read the same numbers (pageGeometry.ts
            // and _page_size in document_rendition.py). Two places to set it is two places to
            // disagree about what shape the signed copy is.
            {label: "Page orientation", icon: "screen_rotation",
             onClick: editable ? () => setPageOptions(true) : undefined,
             unavailable: editable ? undefined : "This revision is not open for editing."},
            {label: "Table", icon: "table", divider: true, submenu: TABLE_ACTIONS},
            {label: "Image", icon: "image", submenu: IMAGE_ACTIONS},
            {label: "Borders & lines", icon: "border_all", submenu: [
                // THEY OPEN THE PANEL THAT HOLDS THEM rather than duplicating the controls: the
                // weight, the colour and the dash belong together, and two of the three being in
                // a menu and one in a panel is how they drift apart.
                {label: "Border weight", icon: "line_weight",
                 onClick: inTable ? () => setTableOptions(true) : undefined,
                 unavailable: inTable ? undefined : BORDERS_REASON},
                {label: "Border dash", icon: "line_style",
                 onClick: inTable ? () => setTableOptions(true) : undefined,
                 unavailable: inTable ? undefined : BORDERS_REASON},
            ]},
            {label: "Clear formatting", icon: "format_clear", shortcut: "⌘\\", divider: true,
             onClick: () => chain()?.clearNodes().unsetAllMarks().run()},
        ]},
        {name: "Tools", items: [
            {label: "Word count", icon: "numbers", onClick: words},
            {label: "Spelling", icon: "spellcheck", unavailable:
                "Your browser's, and it is already on — right-click a word it has underlined."},
            {label: "Editing — changes go straight in", icon: "edit", divider: true,
             checked: mode === "editing",
             onClick: editable ? () => setMode("editing") : undefined,
             unavailable: editable ? undefined : "This revision is not open for editing."},
            {label: "Suggesting — changes become proposals", icon: "rate_review", checked: mode === "suggesting",
             onClick: editable ? () => setMode("suggesting") : undefined,
             unavailable: editable ? undefined : "This revision is not open for editing."},
            {label: `Accept all suggestions${outstanding ? ` (${outstanding})` : ""}`, icon: "done_all",
             onClick: editable && outstanding > 0 ? () => resolveAll(editor, true) : undefined,
             unavailable: !editable ? "This revision is not open for editing."
                        : outstanding === 0 ? "Nothing is outstanding." : undefined},
            {label: "Reject all suggestions", icon: "close",
             onClick: editable && outstanding > 0 ? () => resolveAll(editor, false) : undefined,
             unavailable: !editable ? "This revision is not open for editing."
                        : outstanding === 0 ? "Nothing is outstanding." : undefined},
            {label: "Version history", icon: "history", checked: history,
             onClick: () => { setHistory(h => !h); setViewing(null); setViewingBody(null); }},
        ]},
        {name: "Help", items: [
            // THE GUIDES, FROM INSIDE THE EDITOR. The Documentation page is where they are listed,
            // but the person who needs one is in a document, not on a menu page -- and a guide is
            // meant to be read beside the thing it describes, which is why it opens in its own
            // window rather than navigating away from the document.
            {label: "User guides", icon: "menu_book", divider: true,
             onClick: () => window.open("/docs/writing-a-document.html", "parkourWritingDocs",
                                        "width=1200,height=900,scrollbars=yes,resizable=yes")},
            {label: "Keyboard shortcuts", icon: "keyboard", onClick: () => setNotice(
                "⌘B bold · ⌘I italic · ⌘U underline · ⌘Z undo · ⇧⌘Z redo · ⌘F find · "
                + "⌘⇧7 numbered list · ⌘⇧8 bulleted list · Tab indents inside a list")},
            {label: "About ParkourAI Docs", icon: "info", onClick: () => setNotice(
                "ParkourAI Docs holds the TEXT of a controlled document. The record — its number, "
                + "its revision, its lifecycle, its approvals — belongs to QMS, and this app asks "
                + "QMS on every save whether the revision may still change.")},
        ]},
    ];

    // ---------------------------------------------------------------- load

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const content = await api.get(documentUSID);
                if (cancelled) return;
                digest.current = content.ContentDigest ?? null;
                // WHAT THIS EDITOR'S STATE IS BUILT FROM. The provider compares it with every peer
                // that offers a whole document, and refuses the ones built on something else.
                provider.current?.setBaseDigest(digest.current);
                if (content.Body) lastBody.current = JSON.stringify(pruned(content.Body));
                // THE FIGURES' URLS ARRIVE WITH THE TEXT, one round trip for all of them. Recorded
                // before the body is applied so the pictures paint on the first render rather than
                // appearing one at a time afterwards.
                rememberFigures(content.Images);
                setEditable(Boolean(content.Editable));
                setReason(content.Reason ?? null);

                // SEEDED ONLY IF NOBODY ELSE IS ALREADY HOLDING THIS DOCUMENT.
                //
                // A second browser joining a live session receives the whole document over MQTT.
                // Applying the fetched copy on top of it inserts the text a SECOND time -- a CRDT
                // merges two copies rather than recognising them as the same words, so the document
                // silently doubles and the two windows diverge for good.
                //
                // Checking `editor.isEmpty` at this moment is not enough, and that was the bug: the
                // HTTP fetch resolves well before the MQTT hello round-trip, so the editor IS empty
                // and the guard passes. The peers have to be given a moment to answer first.
                if (!seeded.current && content.Exists && editor) {
                    seeded.current = true;
                    // AND THE GRACE DOES NOT START UNTIL THE QUESTION HAS BEEN ASKED. Waiting
                    // 1500ms from the HTTP response assumed the hello had gone out; it had not --
                    // the MQTT socket is often still opening when the editor mounts, and a hello
                    // published before the client connects is dropped with nowhere to go. The
                    // silence that followed was then read as "nobody else has this document",
                    // which is the one reading that leads to seeding on top of a live one.
                    //
                    // So: wait for the provider to have actually announced, THEN give peers the
                    // grace period, and only then decide. If it never manages to announce -- no
                    // MQTT at all, a tenant with the topic ungranted -- the deadline below still
                    // seeds, because a solo author staring at a blank page is the worse failure
                    // and is the overwhelmingly common case.
                    const deadline = Date.now() + ANNOUNCE_WAIT_MS;
                    const decide = () => {
                        if (!provider.current?.announced && Date.now() < deadline) {
                            window.setTimeout(decide, 100);
                            return;
                        }
                        window.setTimeout(() => {
                            if (provider.current?.hasPeerState) return;   // somebody else has it
                            if (!editor.isEmpty) return;                  // or it arrived meanwhile
                            // FLAGGED AS A LOAD, NOT AN EDIT. The tracking plugin skips it; see
                            // suggestions.ts for what happened the once it did not.
                            //
                            // AND THE DOC NODE'S OWN ATTRIBUTES, which `.content` leaves behind.
                            // replaceWith takes the CONTENT of the loaded body, so everything
                            // stored on the document itself -- page setup, and now whether its
                            // headings are numbered -- was dropped on every load and silently
                            // replaced by the defaults. A document saved with a 0.75in header
                            // margin came back at 0.5 and nothing said why; numbered headings came
                            // back unnumbered, which is how this was finally noticed.
                            const loaded = editor.schema.nodeFromJSON(content.Body);
                            const seeding = editor.view.state.tr
                                .setMeta("parkourSeeding", true)
                                .setMeta("addToHistory", false)
                                .replaceWith(0, editor.view.state.doc.content.size, loaded.content);
                            for (const [name, value] of Object.entries(loaded.attrs || {})) {
                                if (editor.view.state.doc.attrs[name] !== value) {
                                    seeding.step(new DocAttrStep(name, value));
                                }
                            }
                            editor.view.dispatch(seeding);
                        }, PEER_GRACE_MS);
                    };
                    decide();
                } else {
                    seeded.current = true;
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [documentUSID, editor, api]);

    // THE EDITOR ON THE WINDOW, IN DEVELOPMENT ONLY. Every question about why a menu item is
    // disabled is a question about the selection, and guessing at it from the outside is how an
    // afternoon goes. Stripped from the production build by the environment check.
    useEffect(() => {
        if (process.env.NODE_ENV === "production" || !editor) return;
        (window as any).__parkourEditor = editor;
        return () => { delete (window as any).__parkourEditor; };
    }, [editor]);

    useEffect(() => { staleRef.current = stale; }, [stale]);
    useEffect(() => { peersRef.current = peers; }, [peers]);

    useEffect(() => { editor?.setEditable(editable); }, [editor, editable]);

    useEffect(() => {
        if (!editor) return;
        editor.on("transaction", bumpEditorState);
        return () => { editor.off("transaction", bumpEditorState); };
    }, [editor]);

    // SUGGESTING IS OFF WHEN THE DOCUMENT CLOSES. Leaving it on would mean somebody returning to a
    // released revision finds a mode selected that cannot do anything.
    useEffect(() => { if (!editable) setMode("editing"); }, [editable]);

    // ---------------------------------------------------------------- collaborate

    useEffect(() => {
        if (!mqttClient || !editor) return;
        const realm = sessionContext?.getRealm?.();
        if (!realm) return;        // no tenant, no topic: the policy would not grant it anyway
        // ASSIGNED TO THE REF, which it never was. Everything that asks "has a peer answered?"
        // or "has the hello gone out?" reads provider.current -- and provider.current was null for
        // the life of this component, so every one of those guards silently answered "no". The
        // seeding decision fell back to `editor.isEmpty`, which the comment above it says outright
        // is not enough.
        provider.current = new YMqttProvider({
            client: mqttClient, documentUSID, realm, doc, awareness,
            // SOMEBODY WHO HAS JUST READ THE STORE SAYS THIS COPY IS BEHIND IT. Stop saving before
            // anything else: a stale editor that keeps autosaving is how the stored document gets
            // replaced by an old one.
            onStale: () => setStale(true),
        });
        return () => { provider.current?.destroy(); provider.current = null; };
        // sessionContext is NOT a dependency. Its identity changes on renders that have nothing to
        // do with this document, and every change tore the provider down and built a new one --
        // which is what exposed the unsubscribe race above. The realm is read once, at connect.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mqttClient, editor, documentUSID, doc, awareness]);

    /**
     * RECORD EVERY EDIT, so any moment can be read back rather than only the ones a five-minute
     * snapshot happened to catch. See updateLog.ts.
     *
     * ONLY WHILE EDITABLE: a read-only window produces nothing of its own, and appending is gated
     * server-side, so starting it there would be a refusal every five seconds.
     */
    useEffect(() => {
        if (!editable || !documentUSID) return;
        if (!api.recordUpdates) return;
        const record = api.recordUpdates;
        log.current = startUpdateLog({
            doc,
            send: (updates, checkpoint) => record(documentUSID, updates, checkpoint),
        });
        return () => { log.current?.flush(); log.current?.stop(); log.current = null; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editable, documentUSID, doc]);

    useEffect(() => { activeThreadRef.current = activeThread; }, [activeThread]);
    useEffect(() => {
        resolvedThreadsRef.current = (threads ?? []).filter(t => t.Resolved).map(t => t.USID);
    }, [threads]);

    /** Load the threads whenever the panel opens, and after anything changes one. */
    const loadThreads = useCallback(async () => {
        if (!api.comments) return;
        setThreadsLoading(true);
        try {
            const answer = await api.comments(documentUSID);
            setThreads(answer?.Comments ?? []);
            setThreadProblem(null);
        } catch (error: any) {
            setThreadProblem(error?.response?.data?.Cause || "The comments could not be loaded.");
            setThreads([]);
        } finally {
            setThreadsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentUSID]);

    useEffect(() => { if (comments) void loadThreads(); }, [comments, loadThreads]);

    /**
     * Ask a question about the selected text.
     *
     * THE THREAD IS CREATED FIRST AND THE ANCHOR PLACED SECOND, because the server issues the id.
     * If the mark never lands -- the window closes, the save is refused -- the thread still exists,
     * still has its question, and still says which words it was about, which is why the anchored
     * text is sent with it rather than derived from the document later.
     *
     * THE MARK IS AN ORDINARY EDIT TO THE BODY and rides out on the next save. It does not move the
     * content digest: the server strips comment marks before hashing.
     */
    const addComment = useCallback(() => {
        if (!editor || !api.createComment) return;
        const { from, to, empty } = editor.state.selection;
        if (empty) return;
        // THE SELECTION IS CAPTURED NOW, because the dialog takes the focus and the caret moves.
        setComposing({ from, to,
                       anchored: editor.state.doc.textBetween(from, to, " ").slice(0, 300) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);

    const sendComment = useCallback(async (body: string, mentions: string[]) => {
        if (!editor || !api.createComment || !composing) return;
        const { from, to, anchored } = composing;
        try {
            const thread = await api.createComment(documentUSID, body, anchored, mentions);
            editor.chain().focus().setTextSelection({ from, to })
                  .setMark("comment", { thread: thread.USID }).run();
            setActiveThread(thread.USID);
            setComposing(null);
            setComments(true);
            await loadThreads();
        } catch (error: any) {
            // THE DIALOG STAYS OPEN ON A REFUSAL, holding what they wrote. The commonest one is
            // "X cannot see this document", and losing the question to it would be unkind.
            setThreadProblem(error?.response?.data?.Cause || "The comment could not be added.");
            setComments(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, documentUSID, composing, loadThreads]);

    /** Put the caret on a thread's anchor, so clicking a comment shows what it is about. */
    const selectThread = useCallback((thread: CommentThread) => {
        setActiveThread(thread.USID);
        if (!editor) return;
        let found: { from: number; to: number } | null = null;
        editor.state.doc.descendants((node: any, pos: number) => {
            if (found || !node.isText) return;
            const mark = (node.marks || []).find(
                (one: any) => one.type.name === "comment" && one.attrs?.thread === thread.USID);
            if (mark) found = { from: pos, to: pos + node.nodeSize };
        });
        // A THREAD WHOSE TEXT HAS BEEN DELETED still opens; there is simply nothing to select, and
        // the panel shows what it was about from what was stored with it.
        if (found) editor.chain().focus().setTextSelection(found).run();
    }, [editor]);

    useEffect(() => {
        const onAwareness = () => {
            const others: string[] = [];
            awareness.getStates().forEach((state: any, id: number) => {
                if (id !== doc.clientID && state?.user?.name) others.push(state.user.name);
            });
            setPeers(Array.from(new Set(others)));
        };
        awareness.on("change", onAwareness);
        return () => awareness.off("change", onAwareness);
    }, [awareness, doc]);

    // ----------------------------------------------- figures that arrived without a URL

    /**
     * Resolve the display URL for any figure this window has not got one for.
     *
     * THE COLLABORATION CASE IS WHY THIS EXISTS, not the loading one. A figure inserted by somebody
     * else arrives here over MQTT as a node holding an attachment USID and nothing else -- this
     * window never fetched a body carrying its URL, so without this the other editor sees the
     * picture and everybody else sees a placeholder forever.
     *
     * NOT GATED ON `editable`. A reader has exactly the same problem and none of the save machinery.
     *
     * ASKED AT MOST ONCE A MINUTE PER FIGURE. An attachment that genuinely cannot be resolved --
     * swept, deleted, or never completed -- would otherwise be re-asked on every keystroke, because
     * the thing that makes it unresolved is also what makes it still unresolved next time.
     */
    useEffect(() => {
        if (!editor || !api.figureUrls) return;

        let cancelled = false;
        let timer: any = null;
        const asked = new Map<string, number>();

        const check = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(async () => {
                if (cancelled) return;
                const now = Date.now();
                const missing = unresolvedFigures(editor.getJSON())
                    .filter(one => now - (asked.get(one) ?? 0) > 60_000);
                if (!missing.length) return;
                missing.forEach(one => asked.set(one, now));
                try {
                    const answer = await api.figureUrls?.(documentUSID, missing);
                    if (!cancelled) rememberFigures(answer?.Images);
                } catch {
                    // A FIGURE THAT WILL NOT RESOLVE SHOWS ITS PLACEHOLDER. It is not an error worth
                    // a banner -- the text is intact and the rest of the document is unaffected.
                }
            }, 250);
        };

        editor.on("update", check);
        check();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            editor.off("update", check);
        };
    }, [editor, api, documentUSID]);

    // ---------------------------------------------------------------- save

    useEffect(() => {
        if (!editor || !editable) return;

        const onUpdate = () => {
            if (saveTimer.current) clearTimeout(saveTimer.current);
            saveTimer.current = setTimeout(async () => {
                // NOT WHILE THIS WINDOW IS BEHIND THE STORE. Saving now would write a document
                // built on a version somebody else has already replaced.
                if (staleRef.current) return;
                try {
                    // EVERY EDITOR IN THE ROOM RUNS THIS TIMER, so several will fire at once and all
                    // but the first will carry a digest the server has already moved past. That is
                    // what ExpectedDigest is for: the losers are told the body changed, and simply
                    // reload the digest rather than overwriting each other.
                        const body = pruned(editor.getJSON());
                    const serialised = JSON.stringify(body);
                    // NOTHING TO SAY. A restore is the exception: it carries a Source the trail
                    // needs, and it is only ever scheduled because something really changed.
                    if (serialised === lastBody.current && !pendingSource.current) return;

                    const source = pendingSource.current;
                    pendingSource.current = null;
                    const saved = await api.save(documentUSID, body, digest.current,
                                                 false, source ?? undefined);
                    digest.current = saved.ContentDigest;
                    lastBody.current = serialised;
                    // A CHECKPOINT WHERE THE SNAPSHOT IS. The server decides when to keep a body,
                    // and matching that cadence is what keeps a replay bounded: reconstructing a
                    // moment is "the last checkpoint before it, plus the updates since".
                    if (saved.Snapshot) log.current?.checkpoint();
                    provider.current?.setBaseDigest(digest.current);
                    reportSaved.current?.(Date.now());
                    setSaveError(null);
                    // THE BUDGET IS PER CONTESTED MOMENT, not per session: a save that lands means
                    // this window is writing again, and the next collision starts from zero.
                    refences.current = 0;
                } catch (error: any) {
                    const code = error?.response?.data?.Code;
                    if (code === "CC-DOCS-WIPE") {
                        // STOP, AND ASK. The autosave does not retry: retrying would be the editor
                        // insisting on a change the server just said looks like an accident, and
                        // the whole value of the interlock is that somebody has to answer it.
                        setWipe(error?.response?.data?.Cause
                                || "This save would remove most of the document.");
                        return;
                    }
                    if (code === "CC-DOCS-STAL") {
                        // CLEARING THE DIGEST WAS A WIPE WAITING TO HAPPEN, and still would be.
                        // "Somebody else won; stop fighting over it" dropped the fence -- and the
                        // very next autosave carried no ExpectedDigest and overwrote the newer
                        // document with this window's older one. Nothing below clears the digest;
                        // it is REPLACED with one just read from the store, so the fence stays
                        // armed and the next save is fenced against a version we have actually seen.
                        //
                        // BUT "REFUSED AS STALE" IS NOT THE SAME AS "IS STALE", which is what the
                        // previous version of this assumed, and it made two-person editing fail in
                        // the ordinary case. Both editors in a live session hold the SAME document
                        // -- Yjs keeps them identical -- and both debounce timers fire at once. One
                        // save lands, the other is refused for carrying the digest it superseded,
                        // and that window declared itself permanently stale and stopped saving.
                        // Observed with two windows open: identical text, one of them silently
                        // persisting nothing.
                        //
                        // THE DISCRIMINATOR IS WHETHER ANYBODY ELSE IS HERE. A window with live
                        // peers is a participant: whatever the other one just stored, this window
                        // has already merged, so re-fencing and saving again cannot lose their
                        // words. A window with no peers that is refused really is behind -- there
                        // is nobody it could have merged with -- and that is the case the banner
                        // was written for.
                        const current = await api.get(documentUSID).catch(() => null);
                        const alone = peersRef.current.length === 0;

                        if (current && !alone && refences.current < 5) {
                            refences.current += 1;
                            digest.current = current.ContentDigest;
                            lastBody.current = JSON.stringify(pruned(current.Body ?? {}));
                            provider.current?.setBaseDigest(digest.current);
                            setSaveError(null);
                            // OUR OWN TEXT STILL HAS TO LAND. The store now holds the other
                            // window's version of this moment; ours is the merged one and differs
                            // whenever we are mid-keystroke, so this asks for another pass rather
                            // than waiting for the next thing somebody types.
                            if (JSON.stringify(pruned(editor.getJSON())) !== lastBody.current) {
                                onUpdate();
                            }
                            return;
                        }

                        // Alone and behind, or re-fenced five times and still losing: say so and
                        // stop, which is the one honest thing a window in that state can do.
                        setStale(true);
                        setSaveError(null);
                        return;
                    }
                    // CC-DOCS-LOCK: the revision froze underneath us. Stop offering to edit.
                    if (code === "CC-DOCS-LOCK") {
                        setEditable(false);
                        setReason(error?.response?.data?.Cause
                                  || "This revision is no longer open for editing.");
                        return;
                    }
                    setSaveError("Your last change has not been saved yet. Retrying.");
                }
            }, SAVE_AFTER_MS);
        };

        editor.on("update", onUpdate);
        return () => {
            editor.off("update", onUpdate);
            if (saveTimer.current) clearTimeout(saveTimer.current);
        };
    }, [editor, editable, documentUSID, api]);

    // ---------------------------------------------------------------- render

    if (loading) {
        return <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} sx={{alignItems: "center", flexWrap: "wrap"}}>
                {/* THE STATUS PILL MOVED UP to the record line, beside the number and the
                    revision it is a fact about. What stays here is what is about this SESSION:
                    who else is in the document, and whether it has saved. */}
                {peers.map((name) => (
                    <Chip key={name} size="small" variant="outlined" label={name} />
                ))}
                <Box sx={{ flexGrow: 1 }} />
                {/* THE SAVED TIME MOVED UP to the far right of the record line. It belongs with
                    the facts about the document rather than in the session row beside the peers --
                    and on the right, where a reader's eye only goes when they are checking, which
                    is the only time anybody looks for it. */}
            </Stack>

            {/* The reason comes from t1, verbatim. A sentence written here would drift from the rule
                and end up telling somebody something untrue about why they cannot type. */}
            {!editable && reason && <Alert severity="info">{reason}</Alert>}
            {saveError && <Alert severity="warning">{saveError}</Alert>}

            {notice && <Alert severity="info" onClose={() => setNotice(null)}>{notice}</Alert>}

            {/* NOT DISMISSABLE WITH AN X. The document on screen and the document stored have
                diverged, and closing the message would leave somebody typing into a copy that is
                not being saved with nothing on screen saying so. The two ways out are the two real
                answers: keep what is here, or go and get what is stored. */}
            {wipe && (
                <Alert severity="warning" action={
                    <Stack direction="row" spacing={1}>
                        <Button size="small" color="inherit" onClick={() => {
                            setWipe(null);
                            // Saved WITH the acknowledgement this time, and only this time.
                            api.save(documentUSID, editor?.getJSON(), digest.current, true)
                               .then((saved: any) => { digest.current = saved.ContentDigest;
                                                       reportSaved.current?.(Date.now()); })
                               .catch(() => setWipe("That could not be saved. Reload the page."));
                        }}>Keep what is on screen</Button>
                        <Button size="small" color="inherit"
                                onClick={() => window.location.reload()}>Reload</Button>
                    </Stack>
                }>{wipe}</Alert>
            )}

            {/* THE MENUS CARRY WHAT THE TOOLBAR CANNOT. A toolbar holds the dozen things done
                constantly; importing a Word file, counting the words, finding and replacing and
                printing have no home without a menu bar, and somebody who has used a word
                processor looks under File first. */}
            <EditorMenuBar menus={menus}/>

            {/* THE TOOLBAR IS DRAWN EVEN WHEN THE DOCUMENT IS CLOSED, with every control inert. An
                absent toolbar says "this is not a document"; a dead one says "this is a document
                you cannot edit", which is the true thing and the thing somebody needs to know. */}
            {/* THE TOOLBAR BECOMES ABOUT THE BORDER when one is picked -- see EditorToolbar.
                Computed here because this is where the editor lives; the same value drives the
                Color section of the Table options panel, so the two never disagree. */}
            <EditorToolbar editor={editor} disabled={!editable}
                           border={pickedBorder}
                           onBorder={(width, colour, dash) =>
                               setSelectedBorder(editor, width, colour, dash)}
                           mode={mode} onMode={setMode} outstanding={outstanding}
                           zoom={zoom}
                           onZoom={(z) => { setZoom(z);
                                            try { localStorage.setItem("docs.zoom", String(z)); } catch {} }}
                           onFind={() => setFinding(true)}
                           collapsed={barHidden}
                           onCollapse={(hidden) => { setBarHidden(hidden);
                               try { localStorage.setItem("docs.toolbar", hidden ? "hidden" : "shown"); } catch {} }}/>

            {/* OUT OF THE LAYOUT ENTIRELY. A file input cannot be opened from script unless the
                click lands on the input itself, so it has to exist -- hidden, not absent. */}
            <input ref={importInput} type="file" accept=".docx" hidden
                   onChange={(e) => {
                       const file = e.target.files?.[0];
                       e.target.value = "";          // so choosing the same file twice still fires
                       if (file) importDocx(file);
                   }}/>

            {/* THE SAME TRICK FOR FIGURES. Several at once, because somebody inserting the three
                photographs of a fixture will pick all three. */}
            <input ref={figureInput} type="file" accept={ACCEPT_ATTRIBUTE} multiple hidden
                   onChange={(e) => {
                       const files = Array.from(e.target.files || []);
                       e.target.value = "";
                       if (files.length) insertFigures(files);
                   }}/>

            {/* THE ONE THING A STALE WINDOW MUST DO IS STOP AND SAY SO. It is not an error --
                nothing is broken and nothing has been lost -- but everything typed from here is
                going nowhere, and a window that silently stopped saving would be worse than one
                that overwrote: the person would keep working and lose all of it. */}
            {stale && (
                <Alert severity="warning" action={
                    <Button color="inherit" size="small" onClick={() => window.location.reload()}>
                        Reload
                    </Button>
                }>
                    This document was saved somewhere else while this window was open, so this copy
                    is behind the stored one. Nothing is being saved from here. Reload to pick up
                    the current text — anything typed since will have to be re-applied.
                </Alert>
            )}

            {finding && <FindReplace editor={editor} editable={editable}
                                     onClose={() => setFinding(false)}/>}

            {setup && <PageSetupDialog open={pageOptions} value={setup}
                                       onClose={() => setPageOptions(false)}
                                       onApply={(next) => { setPageSetup(editor, next);
                                                            setPageOptions(false); }}/>}

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    {/* THE RULER LIVES WITH THE PAGE, INSIDE THIS COLUMN. Outside it, both were
                        centred -- but in different boxes: the ruler in the full width and the page
                        in whatever was left once the version-history panel took its 320px. They
                        agreed until the panel opened and then sat 166px apart, which on a ruler is
                        the one failure that makes it worse than having none. */}
                    <EditorRuler fullWidth={fullWidth} editor={editor} editable={editable}
                                 editorState={editorState}/>
                    <DocumentPaper styleVariables={cssVariables(editor?.state?.doc?.attrs?.paragraphStyles)}
                                   editable={editable} fullWidth={fullWidth} showRuler={!fullWidth}
                                   zoom={zoom}
                                   watermark={watermarkFor(status)}
                                   geometry={pageGeometry(setup)}
                                   headerMargin={setup?.headerMargin}
                                   footerMargin={setup?.footerMargin}
                                   // THE AUTOMATIC FOOTER IS THE FALLBACK, not a second footer.
                                   // Once the document carries one of its own, drawing this as
                                   // well would print the identity twice.
                                   footer={ownFooter ? null
                                           : <DocumentFooter record={record} status={status}
                                                             accountName={sessionContext?.getAccountName?.()}/>}>
                        {/* THE PREVIEW REPLACES THE PAGE, it does not sit beside it. Two renderings
                            of one document on screen at once is the arrangement that gets somebody
                            editing the wrong one. */}
                        {viewingBody
                            ? <Box className="ProseMirror" sx={{ pointerEvents: "none" }}
                                   dangerouslySetInnerHTML={{ __html: renderVersion(viewingBody) }}/>
                            : <EditorContent editor={editor} />}
                    </DocumentPaper>
                </Box>

                {/* THE PANEL SITS WHERE THE VERSION HISTORY SITS, so the page narrows rather
                    than being covered -- these are settings you adjust while LOOKING at the table.
                    It closes itself when the caret leaves every table, because a panel describing
                    a table you are no longer in is a panel that lies. */}
                {tableOptions && look && (
                    <TableOptionsPanel look={look} editable={editable}
                                       columnWidthInches={columnWidth(editor)}
                                       verticalAlign={cellAttribute(editor, "verticalAlign")}
                                       cellBackground={cellAttribute(editor, "backgroundColor")}
                                       hasHeaderRow={tableHasHeader(editor)}
                                       onLook={(next) => setTableLook(editor, next)}
                                       onColumnWidth={(inches) => setColumnWidth(editor, inches)}
                                       columnType={currentColumnType(editor)}
                                       onColumnType={(type) => setCurrentColumnType(editor, type)}
                                       onVerticalAlign={(value) => setCellAttribute(editor, "verticalAlign", value)}
                                       onCellBackground={(value) => setCellAttribute(editor, "backgroundColor", value)}
                                       onPinHeader={() => chain()?.toggleHeaderRow().run()}
                                       minRowHeight={rowMinHeight(editor)}
                                       onMinRowHeight={(inches) => setRowMinHeight(editor, inches)}
                                       onQuickLayout={(fraction, align) => applyQuickLayout(editor, fraction, align)}
                                       border={pickedBorder}
                                       onBorder={(width, colour) =>
                                           setSelectedBorder(editor, width, colour,
                                                             pickedBorder?.dash ?? "solid")}
                                       onClose={() => setTableOptions(false)}/>
                )}

                {/* ONE DIALOG FOR WRITING A NOTE AND FOR CHANGING ONE. `at` says which job it
                    is: null inserts at the caret, a position edits the marker that is there. */}
                <TemplateDialog
                    open={templatesOpen}
                    templates={templates}
                    loading={templatesLoading}
                    onClose={() => setTemplatesOpen(false)}
                    onStart={(template) => {
                        setTemplatesOpen(false);
                        onNewFromTemplate?.(template.USID);
                    }}/>

                <FootnoteDialog
                    open={noteEdit !== null}
                    number={noteEdit?.number ?? 1}
                    value={noteEdit?.text ?? ""}
                    onClose={() => setNoteEdit(null)}
                    onSave={(text) => {
                        if (!editor || !noteEdit) return;
                        if (noteEdit.at === null) {
                            (editor.chain().focus() as any).insertFootnote(text).run();
                            return;
                        }
                        (editor.chain().focus() as any).setFootnoteText(noteEdit.at, text).run();
                    }}
                    onRemove={noteEdit?.at === null || noteEdit === null ? undefined : () => {
                        if (!editor || noteEdit?.at === null || noteEdit?.at === undefined) return;
                        // THE MARKER AND THE NOTE GO TOGETHER, because they are one node. A note
                        // with nothing citing it is not a footnote -- it is a stranded sentence at
                        // the bottom of a page.
                        const at = noteEdit.at;
                        const node = editor.state.doc.nodeAt(at);
                        if (!node) return;
                        editor.chain().focus()
                              .deleteRange({ from: at, to: at + node.nodeSize }).run();
                    }}/>

                <CrossReferenceDialog
                    open={referring}
                    entries={editor ? [
                        ...outlineOf(editor.state.doc.toJSON(),
                                     Boolean(editor.state.doc.attrs?.outlineNumbering)),
                        // BOOKMARKS AFTER THE SECTIONS, as their own group: they are a flat list
                        // of named passages and interleaving them with the outline would make the
                        // document's shape unreadable.
                        ...bookmarksOf(editor.state.doc.toJSON(),
                                       Boolean(editor.state.doc.attrs?.outlineNumbering))
                            .map(one => ({ anchor: one.id, level: 1, number: one.number,
                                           text: one.name, bookmark: true })),
                    ] : []}
                    onClose={() => setReferring(false)}
                    onInsert={(index, shows, entry) => {
                        setReferring(false);
                        if (!editor) return;
                        // A BOOKMARK CARRIES ITS OWN ID, placed when it was named, so there is
                        // nothing to assign -- only a heading needs an anchor stamped on first use.
                        if ((entry as any)?.bookmark) {
                            editor.chain().focus()
                                  .insertContent({ type: "crossReference",
                                                   attrs: { target: entry.anchor, shows } })
                                  .run();
                            return;
                        }
                        // THE ANCHOR IS ASSIGNED NOW, not when the heading was written: stamping an
                        // id onto every heading in every document would rewrite every body and
                        // change every digest for an attribute almost none of them would use.
                        let seen = -1;
                        let headingPos: number | null = null;
                        editor.state.doc.forEach((node: any, offset: number) => {
                            if (node.type.name !== "heading") return;
                            seen += 1;
                            if (seen === index && headingPos === null) headingPos = offset;
                        });
                        if (headingPos === null) return;
                        const anchor = ensureAnchor(editor, headingPos);
                        if (!anchor) return;
                        editor.chain().focus()
                              .insertContent({ type: "crossReference",
                                               attrs: { target: anchor, shows } })
                              .run();
                    }}/>

                {outline && (
                    <OutlinePane editor={editor} editorState={editorState}
                                 onClose={() => setOutline(false)}/>
                )}

                {composing && (
                    <CommentComposer anchorText={composing.anchored}
                                     people={(search) =>
                                         api.mentionablePeople?.(documentUSID, search)
                                         ?? Promise.resolve([])}
                                     onCancel={() => setComposing(null)}
                                     onSend={sendComment}/>
                )}

                {comments && (
                    <CommentPanel threads={threads} loading={threadsLoading} problem={threadProblem}
                                  active={activeThread}
                                  onSelect={selectThread}
                                  onReply={async (thread, text, mentions) => {
                                      await api.replyToComment?.(thread.USID, text, mentions);
                                      await loadThreads();
                                  }}
                                  people={(search) =>
                                      api.mentionablePeople?.(documentUSID, search) ?? Promise.resolve([])}
                                  onResolve={async (thread, resolved) => {
                                      await api.resolveComment?.(thread.USID, resolved);
                                      await loadThreads();
                                  }}
                                  onDismissProblem={() => setThreadProblem(null)}
                                  onClose={() => { setComments(false); setActiveThread(null); }}/>
                )}

                {history && (
                    <VersionHistory documentUSID={documentUSID} editable={editable}
                                    replayAt={api.replayAt}
                                    viewing={viewing} viewingBody={viewingBody}
                                    onCopy={api.copyToNewPart}
                                    onView={(version, body) => { setViewing(version); setViewingBody(body); }}
                                    onRestore={restore}
                                    onClose={() => { setHistory(false); setViewing(null); setViewingBody(null); }}/>
                )}
            </Box>

            {mode === "suggesting" && <SuggestionBubble editor={editor} editable={editable}/>}
        </Stack>
    );
}
